import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { marked } from 'marked';
import { FiArrowUp, FiCopy, FiFileText, FiCalendar, FiBookOpen, FiClock, FiExternalLink, FiZap, FiShield, FiX, FiEdit2, FiCheck, FiPlus, FiTrash2, FiSearch, FiChevronRight, FiChevronLeft, FiMessageSquare, FiPaperclip, FiImage, FiFile, FiAlertCircle, FiGitBranch, FiGlobe, FiFolder, FiUpload, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HoverPopover from '../components/common/HoverPopover';
import TimeAgo from '../components/common/TimeAgo';
import { aiAPI, draftsAPI, documentPdfAPI } from '../services/api';
import sanitizeDocHtml from '../utils/sanitizeHtml';
import { DOC_CSS_SCOPED } from '../utils/docCss';
import {
  buildStandaloneHtml, htmlToPlainText, isSameDraft, applyPlaceholderFill,
} from '../utils/draftUtils';
import ExportMenu from '../components/common/ExportMenu';
import { useDocumentExport } from '../hooks/useDocumentExport';
import { draftDocToArtifact } from '../utils/draftToArtifact';
import { saveChatImage, getChatImage, clearChatImages } from '../services/chatMediaCache';
import { getDocumentPreview } from '../utils/documentPreviews';
import AttachDraftPicker from '../components/agent/AttachDraftPicker';
import { ROUTES } from '../constants/constants';
import { useUI } from '../context/UIContext';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useChat, LS_SESSION, LS_TRANSCRIPT, hydratePersistedMessages } from '../context/ChatContext';
// Phase 2 chat-core split: the transcript renderer, cards, attachments and the
// turn-composition hook live in src/new-ui/chat/ so an embedded copilot dock
// can reuse them — this page is now the standalone HOST (hero, history,
// artifact panel, rich composer).
import ChatThread from '../chat/ChatThread';
import { useChatTurn } from '../chat/useChatTurn';
import { buildWireHistory } from '../chat/wire';
import {
  MAX_ATTACHMENTS, ATTACH_ACCEPT, ATTACH_EXT, ATTACH_TYPES_LABEL, uid, CHAT_ATTACH_MAX_CHARS, MAX_ATTACH_BYTES, MAX_ATTACH_MB,
  imageToBase64, attachIcon, localFileType, AttachmentChip, AttachmentPreview,
} from '../chat/attachments';
import {
  DetailsConfirmCard, WebSearchOfferCard, WEB_VERIFY_OFFER_RE, FactsFormCard, placeholderLabel,
} from '../chat/cards';
import {
  withPrefilledValues, attachPersonOptions, attachCompanyOptions, pickedCompanyFrom,
  companyChoiceSentence, threadCompanyFrom,
} from '../chat/placeholderPrefill';
import {
  buildAskLedger, pruneSettledFields, confirmedCompanies, isCompanyConfirmSettled,
  supersededByChatPatch,
} from '../chat/askLedger';

marked.setOptions({ gfm: true, breaks: true });

// Does the composer text contain markdown worth previewing? Plain prose returns
// false (no preview clutter); bold/italic, headings, lists, code, blockquotes,
// links and tables return true so the live preview appears on its own.
const MD_HINT = /(\*\*|__|~~|`|\[[^\]]+\]\([^)]+\)|^#{1,6}\s|^\s*[-*+]\s+\S|^\s*\d+\.\s+\S|^\s*>\s|\|.*\|)/m;
const looksLikeMarkdown = (t) => !!t && t.trim().length > 0 && MD_HINT.test(t);

// Human-friendly relative time for history cards.
function timeAgo(iso) {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// Bucket conversations by recency for a grouped, scannable history list.
const HISTORY_ORDER = ['Today', 'Yesterday', 'Previous 7 days', 'Previous 30 days', 'Older'];
function bucketLabel(iso) {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return 'Older';
  const days = (Date.now() - t) / 86400000;
  if (days < 1) return 'Today';
  if (days < 2) return 'Yesterday';
  if (days < 7) return 'Previous 7 days';
  if (days < 30) return 'Previous 30 days';
  return 'Older';
}
function groupSessions(sessions, query) {
  const q = String(query || '').trim().toLowerCase();
  const filtered = q
    ? sessions.filter((s) => `${s.title || ''} ${s.preview || ''}`.toLowerCase().includes(q))
    : sessions;
  return HISTORY_ORDER
    .map((label) => ({ label, items: filtered.filter((s) => bucketLabel(s.updatedAt) === label) }))
    .filter((g) => g.items.length);
}

// Grow the composer to fit its content (capped), and only show a scrollbar once
// it actually hits the cap — avoids the ugly 1-line scrollbar sliver.
function autoSizeComposer(el) {
  if (!el) return;
  el.style.height = 'auto';
  const natural = el.scrollHeight;
  el.style.height = `${Math.min(natural, 200)}px`;
  el.style.overflowY = natural > 200 ? 'auto' : 'hidden';
}

// Agent navigation directives (clientActions) → app routes.
const NAV_ROUTES = {
  dashboard: ROUTES.DASHBOARD, companies: ROUTES.COMPANIES, calendar: ROUTES.CALENDAR,
  alerts: ROUTES.ALERTS, resolutions: ROUTES.RESOLUTIONS, minutes: ROUTES.MINUTES,
  documents: ROUTES.MY_DOCUMENTS, 'agm-calculator': ROUTES.AGM_CALC,
  'report-generator': ROUTES.REPORT_GENERATOR,
};

// Curated starter prompts shown on the empty-state hero.
const SUGGESTIONS = [
  { icon: FiFileText, label: 'Draft a compliant document', desc: 'Resolutions, minutes & notices', prompt: 'Draft a board resolution under Section 179(3) to open a current bank account and authorise directors to operate it' },
  { icon: FiClock, label: 'Check my deadlines', desc: "Reads your company's filings", prompt: 'What are the upcoming and overdue ROC filing deadlines for my active company?' },
  { icon: FiCalendar, label: 'Deadlines & penalties', desc: 'Exact dates and day-wise fees', prompt: 'When is AOC-4 due for a Pvt Ltd after a 30 September AGM, and what is the late-filing penalty per day?' },
  { icon: FiBookOpen, label: 'Ask a legal question', desc: 'Companies Act, SEBI, FEMA, GST, DPDP', prompt: 'Explain the key DPDP Act 2023 obligations and the concrete steps my company must take to comply' },
  { icon: FiEdit2, label: 'Draft meeting minutes', desc: 'SS-1/SS-2 compliant Board/AGM minutes', prompt: 'Draft board meeting minutes for my active company — I\'ll give you the date, time and agenda' },
  { icon: FiShield, label: "Prepare an auditor's report", desc: 'SA 700 series with CARO & IFC annexures', prompt: "Prepare an Independent Auditor's Report for my active company for FY ended 31st March 2026" },
];

// (Attachment constants/helpers/components + buildWireHistory moved to
// src/new-ui/chat/attachments.jsx and src/new-ui/chat/wire.js — Phase 2 split.)
// (AttachmentPreview + ProposalCard moved to the chat core — Phase 2 split.)

// Human labels for the auto-delete picker (User.aiPreferences.historyAutoDelete).
const HISTORY_WINDOW_LABELS = { '90d': '90 days', '1y': '1 year', '36m': '36 months', forever: 'until you delete them' };

// Map a chat draft artifact (kind) to the DraftDocument documentType + pageOrigin enums.
const DRAFT_TYPE_MAP = {
  resolution: ['resolution', 'resolutions'],
  document: ['document', 'document-library'],
  minutes: ['minutes', 'minutes'],
  report: ['report', 'report-generator'],
};

// (ArtifactCard moved to the chat core — Phase 2 split.)

// The artifact side panel — the document gets its own workspace beside the
// chat (split-view on desktop, overlay drawer on small screens). Actions
// (Save / Copy / Download) live here, not in the conversation.
function ArtifactPanel({ artifact, onClose, onSave, onEdited, onQuote, onOpenStudio, highlight }) {
  // Studio handoff is only offered when the draft carries its prefill payload
  // (chat-generated minutes/report drafts do; library-loaded ones don't).
  const studioLabel = artifact.kind === 'minutes' && artifact.meetingConfig
    ? 'Open in Minutes studio'
    : artifact.kind === 'report' && artifact.reportPrefill
      ? 'Open in Report Generator'
      : null;
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [panelWidth, setPanelWidth] = useState(580);
  // Floating "Ask about this" affordance over a text selection in the page.
  const [selection, setSelection] = useState(null); // { text, x, y }
  const bodyRef = useRef(null);
  const editingRef = useRef(false);
  useEffect(() => { editingRef.current = editing; }, [editing]);

  // (Re)load the document into the editable node whenever the artifact changes.
  // Set via ref (not dangerouslySetInnerHTML) so contentEditable edits survive
  // re-renders (toggling edit mode, saving, resizing, etc.).
  useEffect(() => {
    setSaving(false); setSaved(false); setEditing(false);
    if (bodyRef.current) bodyRef.current.innerHTML = sanitizeDocHtml(artifact.html || '');
  }, [artifact]);

  // Escape exits edit mode if editing, otherwise closes the panel.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (editingRef.current) setEditing(false);
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Latest (possibly edited) document HTML, sanitised.
  const currentHtml = () => sanitizeDocHtml(bodyRef.current?.innerHTML || artifact.html || '');

  // Leaving edit mode commits the edits back to the conversation (the message's
  // draft entry) so the AI's next revision starts from what the user sees.
  const toggleEdit = () => {
    if (editing && onEdited) onEdited(artifact, currentHtml());
    setEditing((ed) => !ed);
    setSaved(false);
  };

  const handleSave = async () => {
    const html = currentHtml();
    if (onEdited) onEdited(artifact, html);
    setSaving(true);
    const ok = await onSave({ ...artifact, html });
    setSaving(false);
    if (ok) setSaved(true);
  };

  // Text selected inside the page (read mode) → offer to quote it in chat.
  const handleMouseUp = () => {
    if (editing || !onQuote) return;
    const sel = window.getSelection();
    const text = sel ? String(sel.toString()).trim() : '';
    if (!text || !bodyRef.current || !sel.rangeCount || !bodyRef.current.contains(sel.anchorNode)) {
      setSelection(null);
      return;
    }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setSelection({ text: text.slice(0, 1200), x: rect.left + rect.width / 2, y: rect.top });
  };

  const quoteSelection = () => {
    if (selection) onQuote(selection.text);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  // "Show in draft": find the quoted passage in the document, flash-highlight
  // it and scroll it into view. Probes with the first line's head so a
  // multi-node selection still matches its starting text node.
  useEffect(() => {
    if (!highlight || !bodyRef.current) return;
    const probe = String(highlight.text || '').split('\n')[0].trim().slice(0, 60);
    if (!probe) return;
    const walker = document.createTreeWalker(bodyRef.current, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const idx = node.textContent.indexOf(probe);
      if (idx < 0) continue;
      const range = document.createRange();
      range.setStart(node, idx);
      range.setEnd(node, Math.min(idx + probe.length, node.textContent.length));
      const mark = document.createElement('mark');
      mark.style.background = 'rgba(176,137,58,0.4)';
      mark.style.borderRadius = '3px';
      try { range.surroundContents(mark); } catch { return; }
      mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => {
        const parent = mark.parentNode;
        if (!parent) return;
        while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
        parent.removeChild(mark);
        parent.normalize();
      }, 2600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [highlight]);

  const handleCopy = () => {
    navigator.clipboard?.writeText(htmlToPlainText(currentHtml()) || artifact.plainText || '');
  };

  const handleDownloadDoc = () => {
    // Full standalone HTML with DOC_CSS embedded → opens in Word with correct
    // letterhead / Times typography, not unstyled text.
    const standalone = buildStandaloneHtml(artifact.title || 'Draft', currentHtml());
    const blob = new Blob([standalone], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(artifact.title || 'draft').replace(/[^\w\s-]/g, '').trim().slice(0, 60) || 'draft'}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // The same export set every other document surface offers (Minutes studio,
  // resolution/document modals) — one shared hook so the panel can't drift.
  // Reads from bodyRef, so a draft the user edited in place exports as edited.
  const {
    copy: exportCopy, downloadTxt, downloadHtml, print: exportPrint,
  } = useDocumentExport(bodyRef, {
    title: artifact.title || 'Draft',
    fallbackHtml: artifact.html || '',
    audit: { resourceType: artifact.kind || 'draft', resourceId: artifact.id || '', surface: 'ai-chat-artifact' },
  });

  // Server-rendered PDF. Falls back to the browser dialog when the headless
  // renderer is unavailable (501) so the action never simply fails.
  const [pdfBusy, setPdfBusy] = useState(false);
  const handleDownloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      await documentPdfAPI.download({
        title: artifact.title || 'Document',
        bodyHtml: currentHtml(),
        kind: artifact.kind || 'document',
        cin: artifact.companyCin || '',
        filename: (artifact.title || 'document').replace(/[^\w\s-]/g, '').trim().slice(0, 60) || 'document',
      });
    } catch (e) {
      exportPrint();
    } finally {
      setPdfBusy(false);
    }
  };

  const exportItems = [
    { key: 'copy', label: 'Copy text', onClick: exportCopy },
    { key: 'txt', label: 'Download .TXT', onClick: downloadTxt },
    { key: 'html', label: 'Download .HTML', onClick: downloadHtml },
    { key: 'doc', label: 'Download .DOC', hint: 'Opens in Word with the letterhead intact', onClick: handleDownloadDoc },
    {
      key: 'pdf',
      label: pdfBusy ? 'Preparing PDF…' : 'Download PDF',
      hint: 'Clean A4 — no browser header or page URL',
      onClick: handleDownloadPdf,
      disabled: pdfBusy,
      dividerBefore: true,
    },
    { key: 'print', label: 'Print / PDF', hint: 'Browser print dialog', onClick: exportPrint },
  ];

  // Drag the left edge to resize the panel (desktop split-view).
  const startResize = (e) => {
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    const onMove = (ev) => {
      const w = Math.min(Math.max(window.innerWidth - ev.clientX, 380), Math.min(window.innerWidth - 320, 1040));
      setPanelWidth(w);
    };
    const onUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <>
      {/* Backdrop — small screens only (panel is an overlay drawer there). */}
      <div className="lg:hidden fixed inset-0 z-[55]" style={{ background: 'rgba(8,17,31,0.4)' }} onClick={onClose} />
      <div
        className="fixed lg:relative inset-y-0 right-0 z-[60] lg:z-auto shrink-0 flex flex-col bg-white border-l animate-in slide-in-from-right-4 fade-in duration-300 ease-out"
        style={{ width: panelWidth, maxWidth: '100vw', borderColor: 'rgba(226,217,200,0.8)' }}
      >
        {/* Resize handle (left edge) — desktop only */}
        <div onMouseDown={startResize} className="hidden lg:flex absolute -left-1.5 top-0 bottom-0 w-3 cursor-col-resize items-center justify-center z-20 group/rz" aria-hidden="true">
          <div className="w-[3px] h-14 rounded-full bg-[#ddd6c6] group-hover/rz:bg-[#145f5f] transition-colors" />
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-b shrink-0" style={{ borderColor: 'rgba(226,217,200,0.7)', background: '#faf8f3' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(20,95,95,0.1)', color: '#145f5f' }}><FiFileText size={14} /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-bold text-[#08111f] truncate">{artifact.title || 'Draft'}</div>
            <div className="text-[10px] uppercase tracking-[0.5px] text-[#9a9080]">{artifact.kind} · {editing ? 'Editing' : 'Draft'}</div>
          </div>
          {artifact.edited && !editing && (
            <span className="text-[9.5px] uppercase tracking-[0.5px] font-semibold px-1.5 py-[2px] rounded" style={{ background: 'rgba(176,137,58,0.12)', color: '#8a6a1f' }}>
              Edited
            </span>
          )}
          <button
            onClick={toggleEdit}
            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 h-8 rounded-lg transition-all active:scale-95"
            style={editing ? { background: '#145f5f', color: '#fff' } : { background: 'transparent', color: '#145f5f' }}
          >
            {editing ? <><FiCheck size={13} /> Done</> : <><FiEdit2 size={13} /> Edit</>}
          </button>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-[#9a9080] hover:text-[#08111f] hover:bg-[#f0ece2] transition-colors" aria-label="Close panel">
            <FiX size={18} />
          </button>
        </div>
        <div
          className="flex-1 overflow-y-auto cs-content-scroll px-6 py-7"
          style={{ background: '#e8e0cf' }}
          onMouseUp={handleMouseUp}
          onScroll={() => selection && setSelection(null)}
        >
          {/* QA-1: deterministic filing-readiness findings for THIS draft —
              placeholders to fill, formatting residue, missing sections. */}
          {artifact.lint?.findings?.length > 0 && (
            <div className="mx-auto mb-3 px-3.5 py-2.5 rounded-[10px] border flex flex-col gap-1.5" style={{ maxWidth: '640px', background: '#fff7e6', borderColor: '#eadfc0' }}>
              <div className="text-[9.5px] uppercase tracking-[0.5px] font-bold" style={{ color: '#8a6a1f' }}>Before filing</div>
              {artifact.lint.findings.slice(0, 4).map((f) => (
                <div key={f.code} className="flex items-start gap-1.5 text-[11px] leading-snug" style={{ color: f.severity === 'warning' ? '#8a6a1f' : '#7a7060' }}>
                  <FiAlertCircle size={12} className="mt-[1px] shrink-0" /> <span>{f.message}</span>
                </div>
              ))}
            </div>
          )}
          {/* A floating "page" on a beige mat. Render with the app's document CSS
              (.doc-letterhead / .doc-doc-title / .doc-para …) so the draft uses the
              real document typography — Times — matching every other preview. */}
          <div
            className="bg-white mx-auto rounded-xl overflow-hidden border transition-shadow"
            style={{
              maxWidth: '640px',
              borderColor: editing ? 'rgba(20,95,95,0.5)' : 'rgba(226,217,200,0.7)',
              boxShadow: editing
                ? '0 0 0 2px rgba(20,95,95,0.35), 0 14px 44px -14px rgba(8,17,31,0.22)'
                : '0 14px 44px -14px rgba(8,17,31,0.22), 0 2px 8px rgba(8,17,31,0.05)',
            }}
          >
            <style>{DOC_CSS_SCOPED}</style>
            <div
              ref={bodyRef}
              className="doc-preview"
              contentEditable={editing}
              suppressContentEditableWarning
              spellCheck={editing}
              onInput={() => { if (saved) setSaved(false); }}
              style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '11.5pt', lineHeight: 1.7, color: '#000', backgroundColor: '#fff', padding: '42px 46px', outline: 'none', cursor: editing ? 'text' : 'default' }}
            />
          </div>
          {editing ? (
            <div className="text-[10.5px] text-center text-[#7a7060] mt-3">Click anywhere in the page to edit · Done commits your changes — the AI works from the edited version</div>
          ) : (
            <div className="text-[10.5px] text-center text-[#9a9080] mt-3">Select any text to ask the AI about it</div>
          )}
        </div>

        {/* Floating quote-in-chat affordance over the current selection */}
        {selection && !editing && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={quoteSelection}
            className="fixed z-[70] inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11.5px] font-semibold text-white shadow-lg transition-transform active:scale-95"
            style={{ background: '#145f5f', left: Math.max(12, selection.x - 60), top: Math.max(12, selection.y - 40) }}
          >
            <FiMessageSquare size={12} /> Ask about this
          </button>
        )}
        <div className="flex items-center gap-2 px-4 py-3 border-t bg-white shrink-0 flex-wrap" style={{ borderColor: 'rgba(226,217,200,0.7)' }}>
          <Button variant="gold" size="sm" onClick={handleSave} disabled={saving || saved} loading={saving} leftIcon={saved ? <FiCheck size={13} /> : null}>
            {saved ? 'Saved' : 'Save to My Documents'}
          </Button>
          <Button variant="ghost" size="sm" leftIcon={<FiCopy size={13} />} onClick={handleCopy}>Copy</Button>
          <ExportMenu items={exportItems} label="Download" size="sm" variant="ghost" align="left" />
          {studioLabel && onOpenStudio && (
            <Button variant="ghost" size="sm" leftIcon={<FiExternalLink size={13} />} onClick={() => onOpenStudio(artifact, currentHtml())}>
              {studioLabel}
            </Button>
          )}
          <span className="text-[10px] text-[#9a9080] ml-auto">Verify before filing</span>
        </div>
      </div>
    </>
  );
}

// (Turn rendering — SOURCE_ROUTES/WebSourceLink — moved to the chat core; Phase 2 split.)

// Forked sessions carry a "[Fork]" title prefix (stamped server-side on
// create). Render it as a proper badge, not raw text.
function SessionTitle({ title }) {
  const raw = String(title || '');
  const isFork = raw.startsWith('[Fork]');
  const text = isFork ? (raw.slice(6).trim() || 'Untitled chat') : raw;
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
      {isFork && (
        <span className="shrink-0 inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.5px] px-1.5 py-[2px] rounded-md" style={{ background: 'rgba(184,151,58,0.14)', color: '#8a6a1f' }}>
          <FiGitBranch size={9} /> Fork
        </span>
      )}
      <span className="truncate">{text}</span>
    </span>
  );
}

// (MsgAction, ask-cards and the Message turn renderer moved to the chat core — Phase 2 split.)


export default function AgentPage() {
  const { showToast } = useUI();
  const { activeCompany, selectCompany, companies, fetchCompanies, noCompanyMode } = useApp();
  const { user } = useAuth();
  const firstName = String(user?.fullName || user?.name || '').trim().split(/\s+/)[0] || '';
  // The conversation lifecycle (transcript, streaming, persistence, reload
  // recovery) lives in ChatContext ABOVE the router, so a generation survives
  // navigating away from /agent and back. This page owns only composition, the
  // artifact panel, navigation and history UI.
  const {
    messages, setMessages,
    loading,
    sessionId, setSessionId,
    consentPersist, setConsentPersist,
    consentTrain, setConsentTrain,
    sendTurn, stopGenerating, resetChat, markForked,
    registerPageHandlers, setConversationOrigin,
  } = useChat();
  // Shared turn composition (quote prefix, attachments, wire history, context)
  // from the chat core — the same hook an embedded copilot dock will use.
  const { composeAndSend } = useChatTurn({ sendTurn });
  // The company THIS CONVERSATION is about, when the user has picked one on the
  // company card. Deliberately separate from the app's activeCompany: the
  // selector always points somewhere (it auto-pins the first company in the
  // portfolio), so it says where the user is standing, not which company a
  // document is for. Picking here binds the chat and leaves the rest of the app
  // where it was. Derived from the transcript so a restored conversation keeps
  // its binding with nothing extra persisted.
  const [threadCompanyCin, setThreadCompanyCin] = useState(null);
  // A sidebar switch made AFTER the chat was bound. Held until the next message
  // carries the new pick into the transcript (the transcript is what a restored
  // chat and the server read), so the two never disagree for more than a turn.
  const pendingSwitchRef = useRef(null);
  useEffect(() => {
    const fromThread = threadCompanyFrom(messages)?.cin || null;
    const pending = pendingSwitchRef.current;
    if (pending && fromThread === pending.cin) pendingSwitchRef.current = null;
    setThreadCompanyCin(pendingSwitchRef.current?.cin || fromThread);
  }, [messages]);

  // Sidebar and chat are one company, in both directions. Selecting a company
  // in the sidebar while this chat is bound to another one moves the chat to
  // it (Ritik, 21 Sep 2026: "if I am working on a specific company the system
  // should pick that company") — and says so, because a document already open
  // on the right still belongs to the previous company.
  const lastActiveCinRef = useRef(String(activeCompany?.cin || '').toUpperCase() || null);
  useEffect(() => {
    const cin = String(activeCompany?.cin || '').toUpperCase() || null;
    const prev = lastActiveCinRef.current;
    lastActiveCinRef.current = cin;
    if (!cin || cin === prev || !threadCompanyCin || threadCompanyCin === cin) return;
    pendingSwitchRef.current = { cin, name: activeCompany?.name || cin };
    setThreadCompanyCin(cin);
    showToast(`This chat now follows ${activeCompany?.name || cin} — new documents will be drafted for it.`, 'info');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.cin]);
  // Resolved against the portfolio so the composer can NAME the chat's company.
  const threadCompany = useMemo(
    () => (threadCompanyCin
      ? companies.find((c) => String(c.cin || '').toUpperCase() === threadCompanyCin) || null
      : null),
    [threadCompanyCin, companies],
  );
  // Composer draft persists across chat switches and reloads (like Claude) —
  // restored from localStorage, only cleared once a message is actually sent.
  const [input, setInput] = useState(() => {
    try { return localStorage.getItem('cs_ai_composer_draft') || ''; } catch { return ''; }
  });
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [activeArtifact, setActiveArtifact] = useState(null);
  // A passage the user selected in the artifact, attached to the next message
  // as a dedicated context chip (Copilot-style) instead of raw composer text.
  const [quotedSelection, setQuotedSelection] = useState(null);
  const [quoteExpanded, setQuoteExpanded] = useState(false);
  // A prompt composed while the AI was still replying — flushed automatically
  // when the turn completes (see the effect near sendMessage).
  const [queuedText, setQueuedText] = useState(null);
  // Docs-as-artifacts: the attach split-menu, the saved-documents picker, and
  // the Update-original / Save-as-copy choice for library docs.
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [draftPickerOpen, setDraftPickerOpen] = useState(false);
  const [saveChoice, setSaveChoice] = useState(null); // { draft, resolve }
  // Bumping the nonce asks the artifact panel to find + flash-highlight the
  // quoted passage inside the document ("Show in draft").
  const [highlightReq, setHighlightReq] = useState(null); // { text, nonce }
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySessions, setHistorySessions] = useState([]);
  const [historyQuery, setHistoryQuery] = useState('');
  // Per-surface history scope (e.g. 'minutes' when opened from the Minutes
  // studio); null = all conversations.
  const [historyKindFilter, setHistoryKindFilter] = useState(null);
  // History has two tabs: 'all' (live chats) and 'deleted' (Recently Deleted bin).
  const [historyTab, setHistoryTab] = useState('all');
  const [deletedSessions, setDeletedSessions] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  // Pending destructive action awaiting confirmation: { kind: 'soft'|'permanent', id, busy }.
  const [confirm, setConfirm] = useState(null);
  // Lightbox over a SENT message's images (ChatGPT-style): { items, id }.
  const [sentPreview, setSentPreview] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // History is its own deep-linkable route (/agent/history) — derive open state
  // from the URL so it's bookmarkable and works with the back button.
  const historyOpen = location.pathname.replace(/\/+$/, '').endsWith('/history');
  const openHistory = () => navigate(ROUTES.AGENT_HISTORY);
  const closeHistory = () => navigate(ROUTES.AGENT);
  // Per-turn attachments: { id, name, fileType, status: extracting|ready|error, text, error }.
  const [attachments, setAttachments] = useState([]);
  const [previewId, setPreviewId] = useState(null); // id of the attachment shown in the preview overlay
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  // Live mirrors so an in-flight async (silent session restore) can tell whether
  // the user has meanwhile started a chat, and avoid clobbering it.
  const liveMsgsRef = useRef(messages);
  const loadingRef = useRef(loading);
  useEffect(() => { liveMsgsRef.current = messages; }, [messages]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  // Keep the composer draft so it's never lost on reload or chat/history switches.
  useEffect(() => { try { localStorage.setItem('cs_ai_composer_draft', input); } catch { /* ignore */ } }, [input]);

  // The hero (centered greeting + composer) shows until the first user turn.
  // A seeded document (opened from My Documents) also starts the conversation —
  // otherwise the hero empty-state would hide the loaded artifact.
  const started = messages.some((m) => m.role === 'user' || m.seeded);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const resetComposerHeight = () => {
    const el = textareaRef.current;
    if (el) { el.style.height = 'auto'; el.style.overflowY = 'hidden'; }
  };

  const focusComposer = () => setTimeout(() => textareaRef.current?.focus(), 60);

  // Focus the composer on first load + size it to fit any restored draft.
  useEffect(() => {
    setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      autoSizeComposer(el);
    }, 60);
  }, []);

  // Jump (not smooth-scroll) to the latest message whenever the conversation
  // pane (re)appears: first mount with a restored transcript, and returning
  // from the History view. Without this the restored chat opens at the top.
  useEffect(() => {
    if (historyOpen || !liveMsgsRef.current.some((m) => m.role === 'user')) return undefined;
    const t = setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' }), 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen]);

  // ── Attachment handling ──
  // Keep a live ref so the unmount cleanup can revoke any image preview URLs.
  const attachmentsRef = useRef(attachments);
  useEffect(() => { attachmentsRef.current = attachments; }, [attachments]);
  useEffect(() => () => {
    attachmentsRef.current.forEach((a) => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
  }, []);

  const patchAttachment = (id, patch) =>
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  // Extract text from one file in-memory (reuses the supporting-document endpoint;
  // the raw file is never persisted). Updates the chip's status in place. The
  // locally-detected fileType is kept (it drives the thumbnail) — we don't let
  // the server's classification override it.
  const extractAttachment = async (id, file, fileType) => {
    try {
      // Images: downscale + base64 locally so a vision model can see them — no
      // server round-trip and no pre-OCR (the backend OCRs only if it must).
      if (fileType === 'image') {
        const { mimeType, data, dataUrl } = await imageToBase64(file);
        patchAttachment(id, { status: 'ready', kind: 'image', mimeType, data, dataUrl });
        return;
      }
      // Text files: extract text in-memory via the supporting-doc endpoint.
      const res = await aiAPI.extractSupportingDocuments([file], { maxChars: CHAT_ATTACH_MAX_CHARS });
      const doc = (res?.data?.documents || res?.documents || [])[0];
      const text = String(doc?.extractedText || '').trim();
      if (!doc || doc.error || !text) {
        patchAttachment(id, { status: 'error', error: doc?.error || 'No readable text found' });
      } else {
        patchAttachment(id, { status: 'ready', kind: 'text', text, truncated: !!doc.truncated });
      }
    } catch (err) {
      // Keep the real reason — friendlyAttachError maps it to a readable line.
      // 413 arrives from nginx as an HTML page, so the status is the only signal.
      const tooBig = err?.statusCode === 413 || err?.status === 413;
      patchAttachment(id, {
        status: 'error',
        error: tooBig ? `File is too large to upload (max ${MAX_ATTACH_MB} MB)` : (err?.message || 'Could not read this file'),
      });
    }
  };

  const addAttachments = (fileList) => {
    const all = Array.from(fileList || []);
    // Reject oversized files here rather than letting them fail mid-upload.
    const supported = all.filter((f) => ATTACH_EXT.test(f.name || ''));
    if (supported.length < all.length) showToast(`Unsupported file — use ${ATTACH_TYPES_LABEL}`, 'warning');
    supported
      .filter((f) => (f.size || 0) > MAX_ATTACH_BYTES)
      .forEach((f) => showToast(`${f.name} is too large — max ${MAX_ATTACH_MB} MB`, 'warning'));
    const picked = supported.filter((f) => (f.size || 0) <= MAX_ATTACH_BYTES);
    if (!picked.length) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) { showToast(`You can attach up to ${MAX_ATTACHMENTS} files`, 'info'); return; }
    const take = picked.slice(0, room);
    const entries = take.map((f) => {
      const fileType = localFileType(f);
      return {
        id: uid(),
        name: f.name,
        fileType,
        size: f.size || 0,
        previewUrl: fileType === 'image' ? URL.createObjectURL(f) : null,
        status: 'extracting',
        text: '',
        error: '',
        // Raw File, kept ONLY on this composer object so a consented send can
        // persist documents for cross-device restore. Never copied onto the
        // sent message (useChatTurn strips to plain fields).
        file: f,
      };
    });
    setAttachments((prev) => [...prev, ...entries]);
    entries.forEach((e, i) => extractAttachment(e.id, take[i], e.fileType));
    if (picked.length > room) showToast(`Only ${room} more file(s) could be added`, 'info');
  };

  const removeAttachment = (id) => setAttachments((prev) => {
    const target = prev.find((a) => a.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    return prev.filter((a) => a.id !== id);
  });

  // Revoke all preview URLs and clear (used after a turn is sent).
  const clearAttachments = () => setAttachments((prev) => {
    prev.forEach((a) => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
    return [];
  });

  const onFilePick = (e) => { addAttachments(e.target.files); e.target.value = ''; };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addAttachments(e.dataTransfer.files);
  };

  // Claude-style paste-to-attach: a pasted screenshot or file in the composer
  // becomes an attachment. Plain text falls through to the default paste.
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items.filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
    if (!files.length) return; // text paste → let it through
    const normalized = files.map((f) => {
      if (f.name && ATTACH_EXT.test(f.name)) return f;
      // Clipboard IMAGES often arrive nameless — give them a real filename so the
      // extractor (and our ext filter) treat them as images. Anything else keeps
      // its own File: renaming a pasted .docx to .png made the backend's
      // magic-byte check reject it as a corrupt image.
      if (!String(f.type || '').startsWith('image/')) return f;
      const ext = f.type === 'image/jpeg' ? 'jpg' : 'png';
      return new File([f], `pasted-${uid()}.${ext}`, { type: f.type });
    });
    e.preventDefault();
    addAttachments(normalized); // its ext filter toasts anything unsupported
  };

  const extractingCount = attachments.filter((a) => a.status === 'extracting').length;
  // Derive (don't store) the previewed attachment so it stays live as extraction
  // finishes, and the overlay auto-closes when the attachment is removed/sent.
  const previewAtt = attachments.find((a) => a.id === previewId) || null;

  // Esc closes the history view (navigates back to the chat).
  useEffect(() => {
    if (!historyOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeHistory(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen]);

  const sendMessage = (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed) return;
    // Compose-while-streaming (Claude-style): a prompt sent mid-generation is
    // QUEUED and auto-sends the moment the current reply finishes. The quote
    // chip and attachments stay on the composer, so they ride along when the
    // queued prompt flushes.
    if (loading) {
      setQueuedText((prev) => (prev ? `${prev}\n${trimmed}` : trimmed));
      setInput('');
      resetComposerHeight();
      return;
    }

    // Composition (quote prefix, attachment payload, wire history, context
    // envelope) lives in the shared chat core (useChatTurn) — this host only
    // clears its own UI state. The provider owns the transport + transcript so
    // the answer survives leaving this page.
    // A sidebar switch since the chat was bound goes into the transcript here,
    // the same way an in-chat pick does, so the server drafts for the company
    // the user is looking at and a restored chat remembers it.
    const pendingSwitch = pendingSwitchRef.current;
    const textToSend = pendingSwitch
      ? `${companyChoiceSentence({ name: pendingSwitch.name, cin: pendingSwitch.cin })}\n\n${trimmed}`
      : trimmed;

    composeAndSend({
      text: textToSend,
      quotedSelection,
      attachments,
      messages,
      activeCompanyCin: activeCompany?.cin || null,
      threadCompanyCin,
      webSearch: webSearchOn,
      draftMode,
      companyOverrides,
      consent: { persist: consentPersist, train: consentTrain },
      sessionId,
    });

    setInput('');
    setQuotedSelection(null); // consumed by this turn
    clearAttachments(); // consumed by this turn (revokes image preview URLs)
    resetComposerHeight();
  };

  // Flush the queued prompt as soon as the in-flight reply finishes. Deferred a
  // tick: the provider's loadingRef syncs in a PARENT effect (runs after this
  // child effect), and sendTurn no-ops while that ref still reads true.
  useEffect(() => {
    if (loading || !queuedText) return undefined;
    const q = queuedText;
    setQueuedText(null);
    const t = setTimeout(() => sendMessage(q), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, queuedText]);

  // ── Prompt history cycling (Claude Code style) ──
  // Up at the very start of the composer (or the ▲ button) steps back through
  // this conversation's sent prompts; Down / ▼ steps forward and finally
  // returns whatever was being typed before browsing started.
  const promptHistory = messages.filter((m) => m.role === 'user').map((m) => m.content);
  const histIdxRef = useRef(null); // null = not browsing
  const histDraftRef = useRef('');

  const historyStep = (dir) => {
    const list = promptHistory;
    if (!list.length) return;
    let idx = histIdxRef.current;
    if (idx === null) {
      if (dir > 0) return;
      histDraftRef.current = input;
      idx = list.length - 1;
    } else {
      idx += dir;
    }
    if (idx >= list.length) {
      histIdxRef.current = null;
      setInput(histDraftRef.current);
      return;
    }
    if (idx < 0) idx = 0;
    histIdxRef.current = idx;
    setInput(list[idx]);
  };

  // `onEnter` lets the composer route Enter through its own send wrapper
  // (sendTyped, which applies the card-supersede rule). Defined further down —
  // hence the injection rather than a direct call.
  const handleKeyDown = (e, onEnter) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      (onEnter || sendMessage)();
      return;
    }
    const el = e.target;
    if (e.key === 'ArrowUp' && el.selectionStart === 0 && el.selectionEnd === 0) {
      e.preventDefault();
      historyStep(-1);
    } else if (e.key === 'ArrowDown' && histIdxRef.current !== null && el.selectionEnd === el.value.length) {
      e.preventDefault();
      historyStep(1);
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    // Typing exits history-browsing (the edited text becomes the live draft).
    if (histIdxRef.current !== null) { histIdxRef.current = null; }
    // Sizing is handled centrally by the layout effect below (covers typing,
    // paste, programmatic fills, and view switches uniformly).
  };

  // Web search opt-in (per conversation, off by default). When on, the model
  // may call the web_search tool; replies then carry clickable citations.
  // Web search opt-in survives refresh and page switches (device-local).
  // DC-3 / QA-3.4: drafting mode — Guided (DEFAULT) front-loads ONE confirm
  // round (details card + questions); AUTO drafts immediately, looks things up
  // as needed, and asks after. Persisted like the web toggle.
  // NOTE: the stored/wire value stays 'quick' for Auto — label-only rename, so
  // no migration and old sessions keep working.
  const [draftMode, setDraftMode] = useState(() => {
    try { return localStorage.getItem('cs_ai_draft_mode') === 'quick' ? 'quick' : 'guided'; } catch { return 'guided'; }
  });
  useEffect(() => {
    try { localStorage.setItem('cs_ai_draft_mode', draftMode); } catch { /* ignore */ }
  }, [draftMode]);

  // DC-1: session-scoped company corrections (per-CIN) confirmed via the
  // details card. Sent with every turn; cleared on new chat / switching
  // sessions so corrections never bleed across conversations. Mirrored to LS
  // so a mid-chat reload keeps them.
  const [companyOverrides, setCompanyOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cs_ai_company_overrides') || 'null') || {}; } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('cs_ai_company_overrides', JSON.stringify(companyOverrides)); } catch { /* ignore */ }
  }, [companyOverrides]);

  // QA-3.2 — Web posture is a mode-aware DEFAULT, never a silent override.
  // Auto → web on (Vee verifies Class-2 claims unasked, ChatGPT-style);
  // Guided → web off (more deliberate). The chip always shows the REAL state,
  // and the moment the user flips it themselves that choice is sticky and wins
  // over the mode forever after — the toggle never lies about what we do.
  // (`cs_ai_web_search` alone can't prove intent: the old mirror effect wrote it
  // on mount, so a separate explicit marker is the honest signal.)
  const webDefaultForMode = (mode) => mode === 'quick';
  const [webExplicit, setWebExplicit] = useState(() => {
    try { return localStorage.getItem('cs_ai_web_explicit') === '1'; } catch { return false; }
  });
  const [webSearchOn, setWebSearchOn] = useState(() => {
    try {
      if (localStorage.getItem('cs_ai_web_explicit') === '1') {
        return localStorage.getItem('cs_ai_web_search') === '1';
      }
      return webDefaultForMode(localStorage.getItem('cs_ai_draft_mode') === 'quick' ? 'quick' : 'guided');
    } catch { return false; }
  });
  // No explicit choice yet → the mode drives the default, live.
  useEffect(() => {
    if (webExplicit) return;
    setWebSearchOn(webDefaultForMode(draftMode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftMode, webExplicit]);
  // Only persist once it's genuinely the user's own choice.
  useEffect(() => {
    if (!webExplicit) return;
    try {
      localStorage.setItem('cs_ai_web_search', webSearchOn ? '1' : '0');
      localStorage.setItem('cs_ai_web_explicit', '1');
    } catch { /* ignore */ }
  }, [webSearchOn, webExplicit]);
  const toggleWebSearch = (next) => {
    setWebExplicit(true);
    setWebSearchOn((v) => (typeof next === 'boolean' ? next : !v));
  };

  // Single source of truth for the composer auto-grow + scroll. Re-sizes on any
  // path that can change the text or remount the textarea (typing, paste,
  // programmatic fills, view switches, attachment rows) — so it can never be
  // left uncapped the way the per-handler approach was.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => { autoSizeComposer(textareaRef.current); }, [input, historyOpen, started, attachments.length]);

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content)
      .then(() => showToast('Copied to clipboard!', 'success'))
      .catch(() => showToast('Copy failed', 'error'));
  };

  // Edit a past user message and resend from that point (ChatGPT-style
  // versions): the replaced branch — the old text plus every message after it —
  // is kept on the message as a version, so ‹ x/y › arrows can flip between
  // the edit and the original conversation from that point.
  const handleEditResend = (index, newText) => {
    if (loading) return;
    const original = messages[index];
    if (!original || original.role !== 'user') return;
    const base = messages.slice(0, index);
    const tail = messages.slice(index + 1).filter((m) => !m.loading && !m.recovering && !m.recoverFailed);

    const versions = (original.versions || [{ content: original.content, tail: null }]).slice();
    const curIdx = original.current ?? versions.length - 1;
    versions[curIdx] = { content: original.content, tail }; // park the live branch
    versions.push({ content: newText, tail: null }); // the new live branch

    const userMsg = { ...original, content: newText, versions, current: versions.length - 1 };
    const history = buildWireHistory([...base, userMsg]);
    setMessages(base);
    setActiveArtifact(null);
    sendTurn({
      userMsg,
      history,
      context: {
        platform: 'complyv',
        activeCompanyCin: activeCompany?.cin || null,
        ...(threadCompanyCin ? { threadCompanyCin } : {}),
        webSearch: webSearchOn,
        draftMode,
        ...(Object.keys(companyOverrides).length ? { companyOverrides } : {}),
      },
      consent: { persist: consentPersist, train: consentTrain },
      sessionId,
      userText: newText,
    });
  };

  // Fork the conversation from an assistant reply (ChatGPT's "branch to a new
  // chat"): keep the transcript up to that reply — including the currently
  // shown version of any edited message — and continue as a NEW conversation.
  // The original stays intact in History (server-side persistence untouched).
  const handleBranchFrom = (index) => {
    if (loading) return;
    // Strip version baggage from the fork — the branch carries only what's on
    // screen, so parked tails from other versions don't ride along.
    const slice = messages.slice(0, index + 1).map(({ versions, current, ...m }) => m);
    setMessages(slice);
    setSessionId(null);
    markForked(); // the saved copy gets a "[Fork]" mark in its name
    setActiveArtifact(null);
    showToast('Branched into a new chat from this reply', 'success');
  };

  // Flip an edited message to another of its versions: park the live branch's
  // tail in its slot, then restore the target version's content + tail.
  const handleSwitchVersion = (index, targetIdx) => {
    if (loading) return;
    const msg = messages[index];
    const versions = (msg?.versions || []).slice();
    const curIdx = msg?.current ?? versions.length - 1;
    if (!versions[targetIdx] || targetIdx === curIdx) return;
    versions[curIdx] = { content: msg.content, tail: messages.slice(index + 1) };
    const target = versions[targetIdx];
    const restored = {
      ...msg,
      content: target.content,
      versions: versions.map((v, k) => (k === targetIdx ? { content: v.content, tail: null } : v)),
      current: targetIdx,
    };
    setActiveArtifact(null);
    setMessages([...messages.slice(0, index), restored, ...(target.tail || [])]);
  };

  // The user finished editing the document in the artifact panel: propagate the
  // edited content back onto the message's draft entry so (a) reopening the
  // card shows the edits and (b) the wire history's draft trace — the AI's
  // revision baseline — is the edited text, not the stale original.
  const handleArtifactEdited = (artifact, html) => {
    const plainText = htmlToPlainText(html) || artifact.plainText || '';
    if (html === artifact.html) return; // nothing changed
    const updated = { ...artifact, html, plainText, edited: true };
    setActiveArtifact(updated);
    setMessages((prev) => prev.map((m) => {
      if (m.role !== 'assistant' || !Array.isArray(m.drafts) || !m.drafts.includes(artifact)) return m;
      return { ...m, drafts: m.drafts.map((d) => (d === artifact ? updated : d)) };
    }));
  };

  // Quote a selected artifact passage — becomes a context chip on the composer
  // (dedicated UI, not raw text); the quote is prepended to the outgoing
  // message when the user sends.
  const handleQuoteSelection = (text) => {
    const quoted = String(text || '').trim();
    if (!quoted) return;
    setQuotedSelection(quoted);
    setQuoteExpanded(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // Hand a chat-generated minutes/report draft off to its studio page,
  // prefilled — the artifact carries the config echo the page restores from.
  // The studio is keyed to the ACTIVE company (its shared DraftSession), so a
  // draft for a different company switches the workspace first.
  const handleOpenStudio = (artifact, html) => {
    const draftHtml = html || artifact.html || '';
    if (artifact.companyCin && artifact.companyCin !== activeCompany?.cin) {
      const target = (companies || []).find((c) => String(c.cin || '').toUpperCase() === String(artifact.companyCin).toUpperCase());
      if (target) selectCompany(target);
    }
    if (artifact.kind === 'minutes' && artifact.meetingConfig) {
      navigate(ROUTES.MINUTES, {
        state: { chatHandoff: { meetingConfig: artifact.meetingConfig, draftHtml, source: artifact.source, title: artifact.title } },
      });
    } else if (artifact.kind === 'report' && artifact.reportPrefill) {
      navigate(ROUTES.REPORT_GENERATOR, {
        state: { reportPrefill: { ...artifact.reportPrefill, draftHtml } },
      });
    }
  };

  // Apply agent UI directives (navigation / company switch) on receipt.
  const handleClientActions = (actions) => {
    for (const a of actions || []) {
      if (a.type === 'navigate' && NAV_ROUTES[a.destination]) {
        navigate(NAV_ROUTES[a.destination]);
        showToast(`Opening ${String(a.destination).replace(/-/g, ' ')}…`, 'info');
      } else if (a.type === 'switch_company') {
        // Actually switch the active workspace so the UI reflects the agent's action.
        const target = (companies || []).find((c) =>
          (a.cin && String(c.cin || '').toUpperCase() === String(a.cin).toUpperCase()) ||
          (a.name && String(c.name || '').trim().toLowerCase() === String(a.name).trim().toLowerCase()),
        );
        if (target) {
          selectCompany(target);
          showToast(`Switched to ${target.name}`, 'success');
        } else {
          showToast(a.name ? `Couldn't find "${a.name}" in your portfolio` : 'Company not found', 'warning');
        }
      }
    }
  };

  // Expose this page's side effects to the chat provider so it can fire them while
  // AgentPage is mounted — and silently skip them when it isn't (the provider keeps
  // streaming into the transcript even after we navigate away). Re-registered every
  // render so the closures (navigate, companies, …) stay fresh; cleared on unmount.
  useEffect(() => registerPageHandlers({
    onClientActions: handleClientActions,
    // set_company_details applied a correction with no card — persist it as a
    // session override so later turns keep rendering the corrected values.
    onCompanyOverrides: (updates) => setCompanyOverrides((prev) => {
      const next = { ...prev };
      for (const u of updates || []) {
        const cin = String(u?.companyCin || '').toUpperCase();
        if (!cin || !u?.values) continue;
        next[cin] = { ...(next[cin] || {}), ...u.values };
      }
      return next;
    }),
    onArtifact: (draft) => setActiveArtifact(draft),
    onToast: showToast,
    onScroll: () => setTimeout(scrollToBottom, 0),
  }));

  // Re-sync the app to a backend change a confirmed action just made, so the UI
  // reflects it without a manual reload.
  const syncAfterAction = async (action, params, result) => {
    try {
      // set_compliance_date edits the Company doc (AGM/FY-end → recomputes every
      // deadline). Refresh the shared portfolio so Dashboard/Alerts/Calendar/
      // Companies all show the new dates immediately.
      if (['set_compliance_date', 'update_company_details'].includes(action) && typeof fetchCompanies === 'function') {
        await fetchCompanies();
      }
      // add_company → fetch the new portfolio. If the action actually added or
      // claimed a company (not just an access-request), make it the active one
      // so the rest of the app immediately reflects "you can work on it now".
      if (action === 'add_company' && typeof fetchCompanies === 'function') {
        await fetchCompanies();
        const outcome = result?.outcome;
        const cin = String(result?.cin || '').toUpperCase();
        if (cin && (outcome === 'created' || outcome === 'claimed' || outcome === 'already_has_access')) {
          // companies may not include the new one until React commits the fetch
          // result — selectCompany is idempotent so a follow-up render also resolves.
          selectCompany({ cin });
        }
      }
    } catch { /* best-effort; the toast already confirmed success */ }
    // Broadcast so any mounted page (e.g. Manage Collaborators, which listens for
    // assign_professional) can refetch its own data to stay in sync.
    try {
      window.dispatchEvent(new CustomEvent('cs:agent-action-executed', { detail: { action, params, result } }));
    } catch { /* CustomEvent unsupported — non-fatal */ }
  };

  // Submit a model-requested facts form (request_facts): compose one compact
  // reply so the model drafts with everything in hand. `otherText` carries a
  // free-form answer / extra instructions (Claude-Code-style "Other").
  const patchFactsCard = (msgIndex, cardIndex, patch) => {
    setMessages((prev) => prev.map((m, i) => (i !== msgIndex ? m : {
      ...m,
      factsRequests: (m.factsRequests || []).map((c, ci) => (ci !== cardIndex ? c : { ...c, ...patch })),
    })));
  };

  const markFactsCard = (msgIndex, cardIndex, note, answers) => patchFactsCard(msgIndex, cardIndex, {
    // `answers` is what makes the thread remember this question was settled
    // (see chat/askLedger.js) — without it the same field is asked again on
    // the next turn, which is exactly the T8.3 respawning form.
    completed: true, completedNote: note, ...(answers ? { answers } : {}),
  });

  // ── One ask, one answer ─────────────────────────────────────────────────
  // The composer is never locked while a card is docked, so two paths can reach
  // the same card inside one tick — a Submit click and a typed message that
  // supersedes it — and a card holding a closure from an earlier render could
  // fire again after the card was settled. Claim the ask before acting: first
  // caller wins, later ones are no-ops. Keyed on the object the ask lives on —
  // the stored card, or the DRAFT for the placeholder-fill form, whose card
  // object is synthesized fresh on every render and so has no usable identity.
  const handledAskRef = useRef(new WeakSet());
  const claimAsk = (key) => {
    if (!key || typeof key !== 'object') return true; // nothing to key on — don't block the user
    if (handledAskRef.current.has(key)) return false;
    handledAskRef.current.add(key);
    return true;
  };
  // Hand the claim back when the action turned out to be a no-op, so a form
  // whose values matched nothing in the document can still be retried.
  const releaseAsk = (key) => {
    if (key && typeof key === 'object') handledAskRef.current.delete(key);
  };
  // The identity to claim a facts ask by. NOT pendingAsk.card — that is a copy
  // (its fields are pruned + person-annotated), rebuilt every render, so it
  // would never match itself. The STORED card is the one thing that persists.
  const storedFactsCard = (msgIndex, cardIndex) => (messages[msgIndex]?.factsRequests || [])[cardIndex] || null;

  const handleSubmitFacts = (msgIndex, cardIndex, card, values, otherText) => {
    const lines = (card.fields || [])
      .map((f) => ({ f, v: String(values[f.key] || '').trim() }))
      .filter(({ v }) => v)
      .map(({ f, v }) => `- ${f.label}: ${v}`);
    if (otherText) lines.push(`- Also: ${otherText}`);
    if (!lines.length) return;
    if (!claimAsk(storedFactsCard(msgIndex, cardIndex))) return;
    const ackLines = takeDetailsAckLines(msgIndex);
    markFactsCard(msgIndex, cardIndex, 'Details submitted.', values);
    // A company pick binds the conversation. The CIN goes into the message
    // itself, not just into state: the transcript is the only thing a restored
    // chat has, so the binding has to survive there — and it is what lets the
    // model call the drafting tools with a verified CIN on this very turn.
    const picked = pickedCompanyFrom(card.fields, values);
    if (picked) {
      setThreadCompanyCin(picked.cin);
      // One company for the sidebar and the chat: a pick here moves the app
      // to it, so nothing else on screen is about a different company.
      const target = (companies || []).find((c) => String(c.cin || '').toUpperCase() === String(picked.cin).toUpperCase());
      if (target && target.cin !== activeCompany?.cin) {
        lastActiveCinRef.current = String(target.cin).toUpperCase();
        selectCompany(target);
      }
    }
    const pickLine = picked ? `${companyChoiceSentence(picked)}\n\n` : '';
    sendMessage(`${ackLines.length ? `${ackLines.join(' ')}\n\n` : ''}${pickLine}Here are the details:\n${lines.join('\n')}`);
  };

  // Skip a model-requested form: the model is waiting for a reply, so tell it
  // to proceed rather than leaving it hanging (carrying any held-back details
  // confirmation with it).
  const handleSkipFacts = (msgIndex, cardIndex) => {
    if (!claimAsk(storedFactsCard(msgIndex, cardIndex))) return;
    const ackLines = takeDetailsAckLines(msgIndex);
    markFactsCard(msgIndex, cardIndex, 'Skipped.');
    sendMessage(`${ackLines.length ? `${ackLines.join(' ')} ` : ''}Skip the remaining questions — proceed with your best judgment and leave placeholders where needed.`);
  };

  // Deterministic post-draft placeholder fill: exact replacement on the
  // document, client-side — the AI is not involved. Partially-filled forms
  // leave the remaining placeholders (and the card re-derives from them).
  const handleFillPlaceholders = (msgIndex, draft, values, otherText) => {
    if (!claimAsk(draft)) return;
    // One implementation, shared with the dock and unit-tested — see
    // applyPlaceholderFill. `applied` is what the document actually took, so
    // the receipt below can never name a value that did not land.
    const { html, plainText, applied } = applyPlaceholderFill(draft, values);
    const filled = applied.length;
    // Free-form instructions go to Vee as a normal message (the deterministic
    // fill can't interpret them). QUEUED, not sent inline: the queue flushes
    // after React commits, so the outgoing wire history carries the FILLED
    // document, not the stale one.
    if (otherText) setQueuedText((prev) => (prev ? `${prev}\n${otherText}` : otherText));
    // Nothing filled = nothing settled: hand the claim back so the form can be
    // retried. (A partial fill replaces the draft object, so the next round of
    // the same form claims cleanly on its own.)
    if (!filled) { releaseAsk(draft); return; }
    const remaining = (draft.lint?.placeholders || []).filter((ph) => !String(values[ph] || '').trim());
    const updated = {
      ...draft,
      html,
      plainText,
      edited: true,
      lint: draft.lint ? {
        ...draft.lint,
        placeholders: remaining,
        findings: (draft.lint.findings || [])
          .filter((f) => f.code !== 'unfilled_placeholders')
          .concat(remaining.length ? [{
            code: 'unfilled_placeholders',
            severity: 'warning',
            message: `${remaining.length} placeholder${remaining.length === 1 ? '' : 's'} need real values before filing: ${remaining.join(', ')}.`,
          }] : []),
      } : draft.lint,
    };
    // A RECEIPT, because this fill is deterministic and produces no reply.
    // Without one the form simply vanished: the values went into the document,
    // the toast faded, and the only thing left in the transcript was whatever
    // the user had typed in "Other" — so a form filled with eight details read
    // back as a single stray note, and looked like nothing else had been taken.
    // The skip path already records one; submitting deserves it more.
    const receipt = {
      title: 'Filled in the remaining details',
      completed: true,
      completedDetail: `${filled} detail${filled === 1 ? '' : 's'} added to the document`,
      fields: applied.map((key) => ({ key, label: placeholderLabel(key) })),
      answers: values,
    };
    setMessages((prev) => prev.map((m, i) => {
      if (i !== msgIndex) return m;
      const next = {
        ...m,
        drafts: m.drafts.map((d) => (isSameDraft(d, draft) ? updated : d)),
        filledPlaceholders: { ...(m.filledPlaceholders || {}), ...values },
        factsRequests: [...(m.factsRequests || []), receipt],
      };
      if (!remaining.length) next.placeholdersFilled = true;
      return next;
    }));
    // ALWAYS show what was just filled — never a conditional swap.
    //
    // The form belongs to ONE document, so after answering it the user must be
    // looking at that document; showing them anything else is wrong by
    // definition. The old conditional only swapped when the panel already held
    // this draft, which fails exactly when it matters: a template is opened
    // ("Convene EGM"), Vee drafts a proper version from it ("Convening of
    // Extraordinary General Meeting"), the fill lands on the new draft — and
    // the panel sits on the template, still showing [DAY, DATE] and [TIME]
    // while the receipt lists seven details as added. Both are true, and the
    // user is reading the wrong document. (The studio dock already did this
    // unconditionally; this brings the agent page in line.)
    setActiveArtifact(updated);
    showToast(`Filled ${filled} detail${filled === 1 ? '' : 's'} into the document`, 'success');
  };

  // Dismiss the post-draft fill form without filling (Skip) — placeholders
  // stay; the "Before filing" strip still lists them.
  const handleDismissFill = (msgIndex, declined = [], draft = null) => {
    if (!claimAsk(draft)) return;
    setMessages((prev) => prev.map((m, i) => (i === msgIndex ? {
      ...m,
      placeholdersFilled: true,
      // WHICH questions were declined, so the next draft doesn't re-open them.
      dismissedPlaceholders: [...new Set([...(m.dismissedPlaceholders || []), ...declined])],
    } : m)));
  };

  // QA-3.2: one-tap consent to the web-verify offer. The tap IS the explicit
  // toggle flip (webExplicit persists it — the chip never lies about state),
  // and the question re-sends via the queue so `context.webSearch` reads the
  // COMMITTED true value — never send synchronously after a state set (the
  // same stale-state trap handleFillPlaceholders avoids).
  const handleAllowWebSearch = (msgIndex) => {
    let userText = '';
    for (let i = msgIndex - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') { userText = messages[i].content; break; }
    }
    setMessages((prev) => prev.map((m, i) => (i === msgIndex ? { ...m, webOfferHandled: true } : m)));
    toggleWebSearch(true);
    if (userText) setQueuedText((prev) => (prev ? `${prev}\n${userText}` : userText));
  };

  const handleDismissWebOffer = (msgIndex) => {
    setMessages((prev) => prev.map((m, i) => (i === msgIndex ? { ...m, webOfferHandled: true } : m)));
  };

  // D-3: explicit thumbs feedback — marks the message locally and ships the
  // exchange (redacted server-side) as an ImprovementSample.
  // Download a reply as Word or PDF (the server sets out the as-on date and sources).
  const handleExport = async (msgIndex, format) => {
    const msg = messages[msgIndex];
    if (!msg || msg.role !== 'assistant') return;
    const heading = (msg.content.match(/^#{1,3}\s+(.+)$/m) || [])[1];
    const title = heading || msg.content.replace(/[#*_>`|-]/g, ' ').trim().split(/\s+/).slice(0, 8).join(' ');
    try {
      await aiAPI.exportAnswer({ format, message: msg.content, title });
    } catch {
      showToast('Could not build the file', 'warning');
    }
  };

  const handleFeedback = async (msgIndex, verdict, reasons) => {
    const msg = messages[msgIndex];
    if (!msg || msg.role !== 'assistant' || msg.feedback) return;
    let userText = '';
    for (let i = msgIndex - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') { userText = messages[i].content; break; }
    }
    setMessages((prev) => prev.map((m, i) => (i === msgIndex ? { ...m, feedback: verdict } : m)));
    try {
      await aiAPI.sendChatFeedback({ sessionId, verdict, reasons, userText, assistantText: msg.content });
    } catch {
      showToast('Could not record feedback', 'warning');
    }
  };

  // DC-2: the user confirmed (or corrected) the details card. Only the EDITED
  // fields become session overrides; a plain confirm stores nothing (the
  // system data is already what will be used). Then a compact message tells
  // Vee to proceed — the corrected values ride the next turn's context.
  const handleConfirmDetails = (msgIndex, cardIndex, card, changedFields, hasEdits) => {
    if (!claimAsk(card)) return;
    if (hasEdits && Object.keys(changedFields).length) {
      setCompanyOverrides((prev) => ({
        ...prev,
        [card.companyCin]: { ...(prev[card.companyCin] || {}), ...changedFields },
      }));
    }
    // When a facts form is pending on the SAME turn (guided mode fires both),
    // don't send yet — the dock advances to step 2 and the facts submit
    // carries ONE combined message. `ackPending` marks what it must include.
    const hasPendingFacts = (messages[msgIndex]?.factsRequests || []).some((c) => !c.completed);
    setMessages((prev) => prev.map((m, i) => (i !== msgIndex ? m : {
      ...m,
      detailsConfirms: (m.detailsConfirms || []).map((c, ci) => (ci !== cardIndex ? c : { ...c, confirmed: true, confirmedWithEdits: hasEdits, ackPending: hasPendingFacts })),
    })));
    if (hasPendingFacts) return;
    const who = card.companyName || card.companyCin;
    sendMessage(hasEdits
      ? `I corrected the company details for ${who} on the card — use the corrected values.`
      : `The company details for ${who} are correct — go ahead.`);
  };

  // Confirmation lines owed from a details card whose message was held back
  // (guided both-cards flow) — prepended to the facts submit/skip, then cleared.
  const takeDetailsAckLines = (msgIndex) => {
    const lines = [];
    (messages[msgIndex]?.detailsConfirms || []).forEach((c) => {
      if (c.confirmed && c.ackPending) {
        const who = c.companyName || c.companyCin;
        lines.push(c.confirmedWithEdits
          ? `I corrected the company details for ${who} on the card — use the corrected values.`
          : `The company details for ${who} are correct.`);
      }
    });
    if (lines.length) {
      setMessages((prev) => prev.map((m, i) => (i !== msgIndex ? m : {
        ...m,
        detailsConfirms: (m.detailsConfirms || []).map((c) => (c.ackPending ? { ...c, ackPending: false } : c)),
      })));
    }
    return lines;
  };

  // Execute a confirmed agent action (the gate fires server-side, re-validated).
  const handleConfirmProposal = async (proposal) => {
    try {
      const res = await aiAPI.executeChatAction({
        action: proposal.action,
        params: proposal.params,
        idempotencyKey: proposal.idempotencyKey,
      });
      const result = res?.data?.result || res?.result || null;
      // add_company can land on four different outcomes — surface each clearly
      // rather than a generic "done", so the user knows what actually happened.
      let toast = 'Done — the action was completed.';
      if (proposal.action === 'add_company') {
        switch (result?.outcome) {
          case 'created': toast = `Added ${result.name || 'company'} to your portfolio.`; break;
          case 'claimed': toast = `Claimed ${result.name || 'company'} — it's now yours.`; break;
          case 'access_requested': toast = 'Access request sent to the company owner.'; break;
          case 'already_has_access': toast = 'You already have access to this company.'; break;
          default: break;
        }
      }
      if (proposal.action === 'update_company_details') toast = 'Company details saved — drafts and registers will use them from now on.';
      showToast(toast, 'success');
      await syncAfterAction(proposal.action, proposal.params, result);
      return true;
    } catch (e) {
      showToast(e?.message || 'Could not complete the action.', 'error');
      // Hand the message back so ProposalCard can keep it on the card.
      return e?.message || false;
    }
  };

  // Persist a chat draft as a NEW DraftDocument (the original save flow).
  const saveDraftAsNew = async (draft) => {
    if (!draft?.companyCin) {
      showToast('Cannot save: this draft is not linked to a company.', 'warning');
      return false;
    }
    const [documentType, pageOrigin] = DRAFT_TYPE_MAP[draft.kind] || ['document', 'document-library'];
    try {
      await draftsAPI.save({
        documentId: `agent-${draft.kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        companyCin: draft.companyCin,
        documentTitle: draft.title || 'AI Draft',
        documentType,
        pageOrigin,
        docSource: draft.edited || draft.fromLibrary ? 'user-edited'
          : draft.source === 'preview' ? 'preview'
            : String(draft.source || '').startsWith('fallback') ? 'fallback' : 'ai',
        docHtml: draft.html || '',
        // Config echoes ride along so the studio pages can restore the full
        // form from My Documents (same shape those pages persist themselves).
        ...(draft.kind === 'minutes' && draft.meetingConfig ? { minutesConfig: draft.meetingConfig } : {}),
        ...(draft.kind === 'report' && draft.reportPrefill ? {
          minutesConfig: {
            kind: 'report',
            reportTypeId: draft.reportPrefill.reportTypeId,
            reportConfig: draft.reportPrefill.reportConfig,
            aiSectionText: draft.reportPrefill.aiSectionText,
            docSource: draft.reportPrefill.docSource,
          },
        } : {}),
      });
      // "Saves are the boundary": a professional saving for a client company
      // makes the document visible to that company's account — say so.
      const isProfessional = user?.roleName === 'professional' || user?.role?.name === 'professional';
      showToast(
        isProfessional && draft.companyCin
          ? 'Saved — visible to this company\'s account in My Documents'
          : 'Saved to My Documents',
        'success',
      );
      return true;
    } catch {
      showToast('Could not save draft. Please try again.', 'error');
      return false;
    }
  };

  // Save from the artifact panel. Library docs (opened from My Documents) get
  // a choice — update the original in place, or keep it and save a copy —
  // resolved by the SaveChoice dialog below. AI-generated drafts save as new.
  const handleSaveDraft = async (draft) => {
    if (draft?.draftId) {
      return new Promise((resolve) => setSaveChoice({ draft, resolve }));
    }
    return saveDraftAsNew(draft);
  };

  // Escape closes the save-choice dialog (same behaviour as ConfirmDialog).
  useEffect(() => {
    if (!saveChoice) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') resolveSaveChoice('cancel'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveChoice]);

  const resolveSaveChoice = async (mode) => {
    const choice = saveChoice;
    setSaveChoice(null);
    if (!choice) return;
    if (mode === 'cancel') { choice.resolve(false); return; }
    if (mode === 'copy') { choice.resolve(await saveDraftAsNew(choice.draft)); return; }
    try {
      await draftsAPI.update(choice.draft.draftId, {
        docHtml: choice.draft.html || '',
        docSource: 'user-edited',
      });
      showToast('Updated in My Documents', 'success');
      choice.resolve(true);
    } catch (err) {
      showToast(err?.message || 'Could not update the document.', 'error');
      choice.resolve(false);
    }
  };

  // ── Docs-as-artifacts: open a saved DraftDocument inside this chat ──
  // The doc becomes a synthetic assistant message carrying the artifact, so the
  // panel opens it AND buildWireHistory emits its plain-text trace — making it
  // the AI's revision baseline with zero backend changes.
  const seedDraftIntoChat = (draftDoc) => {
    const artifact = draftDocToArtifact(draftDoc);
    if (!artifact) {
      showToast('That document has no content to open here.', 'warning');
      return false;
    }
    // The metadata sentence enters the wire history, so the AI answers
    // "who created this / for whom / when" from stated fact.
    const meta = artifact.metaLine ? ` It is ${artifact.metaLine}.` : '';
    const from = artifact.fromLibrary ? 'from My Documents' : 'from the document preview';
    setMessages((prev) => [...prev.filter((m) => !m.loading), {
      role: 'assistant',
      content: `Loaded “${artifact.title}”${artifact.refNumber ? ` (${artifact.refNumber})` : ''} ${from}.${meta} Ask me anything about it, or tell me what to change.`,
      drafts: [artifact],
      seeded: true,
    }]);
    setActiveArtifact(artifact);
    setTimeout(scrollToBottom, 60);
    focusComposer();
    return true;
  };

  // Open a PLATFORM TEMPLATE (resolution / document-library item) in the chat.
  // Rendered fully client-side by getDocumentPreview with the active company's
  // details — same single source of truth the draft modals use. The leading
  // <style> block is stripped; the panel injects its own scoped doc CSS.
  const seedTemplateIntoChat = (tpl) => {
    let html = '';
    // Same precedence as the label and the CIN below — all three must agree, or
    // the document is filled from one company and announced as another.
    try { html = getDocumentPreview(tpl.id, threadCompany || activeCompany || {}) || ''; } catch { html = ''; }
    html = html.replace(/^\s*<style>[\s\S]*?<\/style>\s*/i, '');
    if (!html.trim()) {
      showToast('That template has no preview yet.', 'warning');
      return false;
    }
    const artifact = {
      kind: tpl.kind === 'resolution' ? 'resolution' : 'document',
      title: tpl.title,
      html,
      plainText: htmlToPlainText(html) || '',
      source: 'preview',
      // The conversation's own company wins over the app's selector, same as
      // every other company-scoped path — a template opened in a chat bound to
      // company B must not come back carrying company A's letterhead.
      companyCin: (threadCompany || activeCompany)?.cin || null,
      fromTemplate: true,
    };
    // Whichever company it used, SAY so. A pre-filled document that does not
    // name the company it was filled from is the same silent binding the
    // composer chip used to imply.
    const fillCompany = threadCompany || activeCompany;
    const filledWith = fillCompany?.name
      ? `It's pre-filled with ${fillCompany.name}'s details.`
      : 'Tell me which company it is for and I\'ll fill in its details.';
    setMessages((prev) => [...prev.filter((m) => !m.loading), {
      role: 'assistant',
      content: `Loaded the “${tpl.title}” template from the platform library. ${filledWith} Tell me what to change, or ask me anything about it.`,
      drafts: [artifact],
      seeded: true,
    }]);
    setActiveArtifact(artifact);
    setTimeout(scrollToBottom, 60);
    focusComposer();
    return true;
  };

  // Deep-link: other pages (My Documents "Ask AI", Spotlight) navigate here
  // with { state: { openDraftId | openDraftRef } } — the same handoff pattern
  // My Documents uses for Spotlight refs. Guarded against StrictMode re-invoke;
  // state is cleared so back/refresh won't re-seed.
  const lastSeedHandled = useRef(null);
  useEffect(() => {
    const st = location.state || {};
    const key = st.openDraftId || st.openDraftRef || st.openSessionId
      || (st.agentPrompt && (st._navNonce || st.agentPrompt))
      || (st.openDraftDoc && st.openDraftDoc._navNonce);
    if (!key) { lastSeedHandled.current = null; return; }
    if (lastSeedHandled.current === key) return;
    lastSeedHandled.current = key;
    navigate(location.pathname, { replace: true, state: {} });
    // Reopen a saved conversation (dashboard Recent Conversations strip).
    if (st.openSessionId) {
      loadSession(st.openSessionId);
      return;
    }
    // A prefilled prompt (My Templates "Use in AI Chat") — one Enter away,
    // never auto-sent: no surprise token spend, matches the composer-draft
    // philosophy. Deliberately does NOT reset the chat: the prompt attaches
    // to whatever conversation is open.
    if (st.agentPrompt) {
      setInput(st.agentPrompt);
      setTimeout(() => textareaRef.current?.focus(), 0);
      return;
    }
    showToast('Opening in chat…', 'info');
    // Coming from OUTSIDE the chat (My Documents / Spotlight) means "talk about
    // THIS document" — start a fresh conversation for it. (The in-chat picker
    // deliberately appends instead: that's attaching to the discussion you're
    // already having.) The previous chat stays safe in History.
    resetChat();
    setCompanyOverrides({}); // corrections are per-conversation — never carry into a fresh chat
    setActiveArtifact(null);
    // A doc-shaped payload from a draft modal's "Continue in AI chat" — the
    // content is already in hand (including unsaved on-screen edits), no fetch.
    if (st.openDraftDoc) {
      seedDraftIntoChat(st.openDraftDoc);
      return;
    }
    (async () => {
      try {
        const res = st.openDraftId
          ? await draftsAPI.getById(st.openDraftId)
          : await draftsAPI.getByRef(st.openDraftRef);
        if (res?.success && res.data) seedDraftIntoChat(res.data);
        else showToast('Could not open that document here', 'error');
      } catch (err) {
        showToast(err?.message || 'Could not open that document here', 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Auto-open the panel for a freshly-seeded document. Derived from the
  // transcript (provider state) rather than trusted to the seed callback's own
  // setActiveArtifact: under StrictMode's mount→unmount→mount the callback can
  // target the DEAD page instance, while this effect always runs in the live
  // one. Declared AFTER the deep-link effect so its open is never clobbered by
  // that effect's same-commit setActiveArtifact(null).
  const lastSeedOpenedRef = useRef(null);
  useEffect(() => {
    const last = messages[messages.length - 1];
    const seededDraft = last?.seeded && Array.isArray(last.drafts) ? last.drafts[0] : null;
    if (seededDraft && lastSeedOpenedRef.current !== seededDraft) {
      lastSeedOpenedRef.current = seededDraft;
      setActiveArtifact(seededDraft);
    }
  }, [messages]);

  // Rehydrate image thumbnails on restored conversations from the device-local
  // media cache (the server keeps attachment metadata only — never bytes).
  // Attempted ids are remembered so a cache miss doesn't loop.
  const mediaHydrateAttempted = useRef(new Set());
  useEffect(() => {
    const wanted = [];
    messages.forEach((m) => {
      if (m.role !== 'user' || !Array.isArray(m.attachments)) return;
      m.attachments.forEach((a) => {
        if (a.fileType === 'image' && !a.previewUrl && a.id && !mediaHydrateAttempted.current.has(a.id)) {
          mediaHydrateAttempted.current.add(a.id);
          wanted.push(a.id);
        }
      });
    });
    if (!wanted.length) return;
    (async () => {
      const found = new Map();
      for (const id of wanted) {
        // Device cache first; the server copy covers cross-device restores
        // (and re-primes the local cache so the fetch happens once).
        // eslint-disable-next-line no-await-in-loop
        let dataUrl = await getChatImage(id);
        if (!dataUrl) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const res = await aiAPI.getChatMedia(id);
            dataUrl = res?.data?.dataUrl || null;
            if (dataUrl) saveChatImage(id, dataUrl);
          } catch { /* not persisted (private chat / other user's device) */ }
        }
        if (dataUrl) found.set(id, dataUrl);
      }
      if (!found.size) return;
      setMessages((prev) => prev.map((m) => {
        if (m.role !== 'user' || !Array.isArray(m.attachments)) return m;
        if (!m.attachments.some((a) => found.has(a.id))) return m;
        return {
          ...m,
          attachments: m.attachments.map((a) => (found.has(a.id)
            ? { ...a, previewUrl: found.get(a.id), dataUrl: found.get(a.id) }
            : a)),
        };
      }));
    })();
  }, [messages, setMessages]);

  // Load conversations whenever the history route (/agent/history) becomes
  // active. Stale-while-revalidate: the previously loaded list renders
  // INSTANTLY and refreshes silently in the background — the spinner only
  // exists for the true first load, so reopening history never flashes
  // "Loading…" over content we already have.
  useEffect(() => {
    if (!historyOpen) return undefined;
    setActiveArtifact(null);
    setHistoryQuery('');
    setHistoryTab('all'); // always open on the live list
    // A studio's History button opens this view SCOPED to its document kind
    // (e.g. the Minutes studio → conversations that produced minutes). The
    // NAVIGATION STATE is the single source of truth: the chip's ✕ replace-
    // navigates without it, and the Agent page's own History button never
    // sets it — both land on the unfiltered list.
    const kind = location.state?.historyDraftKind || null;
    if (kind !== historyKindFilter) setHistoryKindFilter(kind);
    if (historySessions.length === 0) setHistoryLoading(true);
    let cancelled = false;
    aiAPI.listChatSessions(kind ? { draftKind: kind } : {})
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.sessions || res?.sessions || [];
        setHistorySessions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (cancelled) return;
        // Keep showing the stale list on a failed refresh — it's better than
        // blanking content the user was just reading.
        if (historySessions.length === 0) showToast('Could not load chat history', 'warning');
      })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen, historyKindFilter, location.state?.historyDraftKind]);

  // Lazy-load the Recently Deleted bin the first time its tab is opened (and on
  // each switch back, so restores/purges elsewhere are reflected). Same
  // stale-while-revalidate as the live list: cached rows render instantly.
  const loadDeletedSessions = useCallback(() => {
    if (deletedSessions.length === 0) setDeletedLoading(true);
    return aiAPI.listChatSessions({ deleted: true })
      .then((res) => {
        const list = res?.data?.sessions || res?.sessions || [];
        setDeletedSessions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (deletedSessions.length === 0) showToast('Could not load Recently Deleted', 'warning');
      })
      .finally(() => setDeletedLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast, deletedSessions.length]);

  useEffect(() => {
    if (historyOpen && historyTab === 'deleted') loadDeletedSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen, historyTab]);

  // silent: used by the on-mount auto-restore (no toast, no history close).
  const loadSession = async (id, { silent = false } = {}) => {
    // Same-session guard: reopening the conversation that's ALREADY live in
    // memory would replace it with the server's linear log — which flattens
    // client-only edit-message versions into separate turns. Keep the richer
    // in-memory copy and just close History. (The server never stored the
    // versions, so a re-fetch can only lose them.)
    if (id && id === sessionId && liveMsgsRef.current.some((m) => m.role === 'user')) {
      if (!silent) { closeHistory(); focusComposer(); }
      return true;
    }
    try {
      const res = await aiAPI.getChatSession(id);
      const session = res?.data?.session || res?.session;
      const msgs = Array.isArray(session?.messages) ? session.messages : [];
      if (!msgs.length) { if (!silent) showToast('This conversation is empty', 'info'); return false; }
      // Race guard: if the user started typing/sending while this silent restore
      // was in flight, don't clobber their in-progress conversation.
      if (silent && (loadingRef.current || liveMsgsRef.current.some((m) => m.role === 'user' || m.seeded))) return false;
      // Rebuild the Sources / Estimate chips from each assistant turn's persisted
      // toolCalls (names) so restored conversations look like live ones. drafts /
      // proposals / attachments come through verbatim so documents reopen.
      const restored = hydratePersistedMessages(msgs);
      setMessages(restored);
      setSessionId(id);
      setConversationOrigin(session.origin || 'agent');
      setActiveArtifact(null);
      // Corrections are per-conversation: switching to ANOTHER session drops
      // them. The silent reload-restore of the live session keeps them (that's
      // the same conversation continuing).
      if (!silent) setCompanyOverrides({});
      if (!silent) {
        closeHistory();
        showToast('Conversation restored', 'success');
      }
      setTimeout(scrollToBottom, 60);
      focusComposer();
      return true;
    } catch {
      if (!silent) showToast('Could not open conversation', 'error');
      return false;
    }
  };

  // On first mount, if the local mirror was empty (fresh device / cleared cache)
  // but we know the last server conversation id, reopen it — so returning to AI
  // Chat lands you back in your previous conversation.
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    if (!consentPersist) return;
    // The local mirror is authoritative when it has a conversation — it carries
    // client-only state (edited-message versions, artifact edits) that the
    // server's linear log flattens. Check BOTH the live state and the raw
    // mirror so an init race can never let the server restore clobber it.
    const startedLocally = messages.some((m) => m.role === 'user' || m.seeded);
    let mirrorHasChat = false;
    try {
      const raw = localStorage.getItem(LS_TRANSCRIPT);
      const parsed = raw ? JSON.parse(raw) : null;
      mirrorHasChat = Array.isArray(parsed) && parsed.some((m) => m.role === 'user' || m.seeded);
    } catch { /* ignore */ }
    if (startedLocally || mirrorHasChat) return;
    let sid = null;
    try { sid = localStorage.getItem(LS_SESSION); } catch { /* ignore */ }
    if (sid) loadSession(sid, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Delete is a two-step, recoverable action now: clicking the trash opens a
  // confirm; confirming SOFT-deletes (moves to Recently Deleted, kept 7 days).
  const askDeleteSession = (id, e) => {
    e.stopPropagation();
    setConfirm({ kind: 'soft', id });
  };
  const askPermanentDelete = (id) => setConfirm({ kind: 'permanent', id });

  // Run whatever destructive action is pending in the confirm dialog.
  const runConfirm = async () => {
    if (!confirm) return;
    const { kind, id } = confirm;
    setConfirm((c) => ({ ...c, busy: true }));
    try {
      if (kind === 'soft') {
        await aiAPI.deleteChatSession(id); // soft delete → Recently Deleted
        setHistorySessions((prev) => prev.filter((s) => s.id !== id));
        // If we just deleted the chat that's currently open, reset the live view
        // so a reload doesn't resurrect it from the local mirror.
        if (id === sessionId) resetChat();
        showToast('Moved to Recently Deleted — restore within 7 days', 'success');
      } else {
        await aiAPI.deleteChatSession(id, { permanent: true });
        setDeletedSessions((prev) => prev.filter((s) => s.id !== id));
        showToast('Conversation permanently deleted', 'success');
      }
      setConfirm(null);
    } catch {
      showToast('Could not delete conversation', 'error');
      setConfirm((c) => (c ? { ...c, busy: false } : null));
    }
  };

  // Delete ALL chat history (DPDP erasure): server sessions + image bytes are
  // cascade-deleted by the endpoint; the device's IndexedDB image cache and
  // the live conversation go with them. Guarded by an explicit confirm.
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const handleDeleteAllChats = async () => {
    setDeletingAll(true);
    try {
      await aiAPI.deleteAllChatHistory();
      clearChatImages();
      setHistorySessions([]);
      setDeletedSessions([]);
      setCompanyOverrides({});
      setActiveArtifact(null);
      resetChat();
      showToast('All chat history deleted', 'success');
    } catch (err) {
      showToast(err?.message || 'Could not delete chat history', 'error');
    } finally {
      setDeletingAll(false);
      setConfirmDeleteAll(false);
    }
  };

  // Restore a chat from Recently Deleted back into history.
  const restoreSession = async (id) => {
    try {
      await aiAPI.restoreChatSession(id);
      setDeletedSessions((prev) => prev.filter((s) => s.id !== id));
      // Refresh the live list so the restored chat reappears under "All chats".
      try {
        const res = await aiAPI.listChatSessions();
        const list = res?.data?.sessions || res?.sessions || [];
        setHistorySessions(Array.isArray(list) ? list : []);
      } catch { /* non-fatal — it shows next time history opens */ }
      showToast('Conversation restored', 'success');
    } catch {
      showToast('Could not restore conversation', 'error');
    }
  };

  // "3d left" / "Purges today" from the server-provided purge date.
  const purgeLabel = (s) => {
    const t = s?.purgesAt ? new Date(s.purgesAt).getTime() : NaN;
    if (Number.isNaN(t)) return '';
    const days = Math.ceil((t - Date.now()) / 86400000);
    if (days <= 0) return 'Purges today';
    return `${days} day${days === 1 ? '' : 's'} left`;
  };

  // Start a fresh conversation (keeps prior chats on the server History). The
  // provider resets the transcript + clears the local mirror / session pointer /
  // any pending-recovery marker; the page handles the UI side (artifact, history).
  const newChat = () => {
    resetChat();
    setCompanyOverrides({}); // corrections are per-conversation
    setActiveArtifact(null);
    if (historyOpen) closeHistory();
    // Keep any unsent draft — it carries into the new chat (persist-even-if-not-sent).
    focusComposer();
  };

  // Which company a draft belongs to. Drafts carry the CIN they were generated
  // against, which is not always the pinned company (Vee can draft for another
  // entity in the same conversation) — match on that first so the fill form is
  // never seeded with a different company's address.
  const companyForDraft = (draft) => {
    const cin = draft?.companyCin;
    if (!cin) return activeCompany || null;
    return companies.find((c) => String(c.cin || '').toUpperCase() === String(cin).toUpperCase())
      || (String(activeCompany?.cin || '').toUpperCase() === String(cin).toUpperCase() ? activeCompany : null);
  };

  // The LATEST pending ask renders docked above the composer (Claude-style,
  // connected to the input), not in the transcript: either a model-requested
  // facts form or the deterministic placeholder fill for the newest draft.
  // Only the last assistant message counts — once the user moves on (a newer
  // user turn exists), an unanswered form is stale and hidden; the transcript
  // keeps only completed receipts.
  const pendingAsk = (() => {
    const m = messages[messages.length - 1];
    if (!m || m.role !== 'assistant' || m.loading) return null;
    const idx = messages.length - 1;
    // A company confirmed ANYWHERE in this thread stays confirmed: the model
    // re-calling confirm_company_details put "Confirm the company" back in
    // front of a user who had confirmed it one turn earlier.
    const confirmedCos = confirmedCompanies(messages);
    const pendingDetailsIdx = (m.detailsConfirms || [])
      .findIndex((c) => !c.confirmed && !isCompanyConfirmSettled(c, confirmedCos));
    // T8.3 — everything this conversation has already settled. A card whose
    // every question was already answered or explicitly skipped is not an ask.
    const ledger = buildAskLedger(messages);
    const pendingFactsIdx = (m.factsRequests || []).findIndex(
      (c) => !c.completed && pruneSettledFields(c.fields || [], ledger).length,
    );
    // One connected panel, stepped: confirm the company first, then the facts.
    const stepsTotal = (pendingDetailsIdx >= 0 ? 1 : 0) + (pendingFactsIdx >= 0 ? 1 : 0);
    if (pendingDetailsIdx >= 0) {
      return {
        type: 'details',
        msgIndex: idx,
        cardIndex: pendingDetailsIdx,
        card: m.detailsConfirms[pendingDetailsIdx],
        step: stepsTotal > 1 ? `Step 1 of ${stepsTotal} · Confirm the company` : 'Confirm the company details',
      };
    }
    if (pendingFactsIdx >= 0) {
      const fr = m.factsRequests[pendingFactsIdx];
      return {
        type: 'facts',
        msgIndex: idx,
        cardIndex: pendingFactsIdx,
        // Fields Vee asked for as `type: 'person'` gain this company's serving
        // directors as picker options, and `type: 'company'` fields gain the
        // user's own portfolio — the model names the question, only the client
        // knows who is on the board or which companies this account holds.
        card: {
          ...fr,
          fields: pruneSettledFields(
            attachCompanyOptions(attachPersonOptions(fr.fields, activeCompany), companies),
            ledger,
          ),
        },
        step: (m.detailsConfirms || []).some((c) => c.confirmed) ? 'Step 2 of 2 · The specifics' : 'Vee needs a few details',
      };
    }
    if (!m.placeholdersFilled && Array.isArray(m.drafts) && m.drafts.length) {
      const d = m.drafts[m.drafts.length - 1];
      // Placeholders already answered or explicitly skipped in this thread are
      // NOT re-asked — that respawning form was T8.3.
      const phs = pruneSettledFields(
        (d?.lint?.placeholders || []).map((ph) => ({ key: ph })),
        ledger,
      ).map((f) => f.key);
      if (phs.length && !/fallback/i.test(d?.source || '')) {
        return {
          type: 'fill',
          msgIndex: idx,
          draft: d,
          step: 'Complete the document',
          card: {
            title: 'Fill in the remaining details',
            subtitle: 'These drop straight into the document — blanks stay as placeholders.',
            // Anything the company record (or the draft's own meeting date)
            // already answers arrives pre-filled — the user edits instead of
            // retyping values the document is printing on the same page.
            fields: withPrefilledValues(
              phs.map((ph) => ({ key: ph, label: placeholderLabel(ph) })),
              { company: companyForDraft(d), draft: d },
            ),
          },
        };
      }
    }
    // QA-3.2: web is off and Vee just offered to verify on the web — surface a
    // one-tap allow instead of sending the user hunting for the toggle. Lowest
    // priority: a details/facts/fill card always outranks a verification nudge.
    if (!webSearchOn && !m.webOfferHandled && WEB_VERIFY_OFFER_RE.test(m.content || '')) {
      return { type: 'web', msgIndex: idx, step: 'Verify on the web' };
    }
    return null;
  })();

  // ── A typed message supersedes the pending ask-card (see chat/askLedger.js) ─
  // The composer is never hidden or locked by the docked panel, so the user can
  // answer in prose instead of in the form. The message wins and the card is
  // settled as SKIPPED — the exact record Skip leaves — so the ledger never
  // re-opens those questions, and the card cannot return later to splice stale
  // answers into a document the newer instruction already changed.
  //
  // Returns any confirmation lines the settled card still owed, to ride in
  // front of the user's text (the same contract handleSkipFacts honours).
  const supersedePendingAsk = () => {
    if (!pendingAsk) return [];
    if (pendingAsk.type === 'facts') {
      if (!claimAsk(storedFactsCard(pendingAsk.msgIndex, pendingAsk.cardIndex))) return [];
      patchFactsCard(pendingAsk.msgIndex, pendingAsk.cardIndex, supersededByChatPatch());
      return takeDetailsAckLines(pendingAsk.msgIndex);
    }
    if (pendingAsk.type === 'fill') {
      if (!claimAsk(pendingAsk.draft)) return [];
      const declined = (pendingAsk.card.fields || []).map((f) => f.key);
      setMessages((prev) => prev.map((m, i) => (i !== pendingAsk.msgIndex ? m : {
        ...m,
        placeholdersFilled: true,
        dismissedPlaceholders: [...new Set([...(m.dismissedPlaceholders || []), ...declined])],
        // The fill form is synthesized from the draft's own placeholders, so
        // there is no stored card to become a receipt. Record one, so the
        // transcript states what happened instead of the form just vanishing.
        factsRequests: [...(m.factsRequests || []), {
          title: 'Fill in the remaining details',
          fields: declined.map((key) => ({ key, label: placeholderLabel(key) })),
          ...supersededByChatPatch(),
        }],
      })));
      return [];
    }
    // 'details' — never auto-confirmed: marking a company verified that the user
    // never looked at would be a false claim, and isCompanyConfirmSettled would
    // then suppress every later card for it. 'web' — an offer, not a question.
    return [];
  };

  // Every user-INITIATED send goes through here: the composer (Enter + the send
  // button), the empty-state suggestion chips, and the "draft using my template"
  // pick. The ask handlers' own synthetic sends deliberately do NOT — they call
  // sendMessage directly, having already settled their own card, and re-entering
  // the supersede would only fight the claim guard.
  const sendTyped = (text) => {
    const trimmed = String(text ?? input).trim();
    if (!trimmed) return;
    const ack = supersedePendingAsk();
    sendMessage(ack.length ? [...ack, trimmed].join('\n') : trimmed);
  };

  // Does typing actually displace what's docked? Only for the cards the
  // supersede rule settles — so the composer hint never over-promises.
  const askIsSuperseding = !!pendingAsk && (pendingAsk.type === 'facts' || pendingAsk.type === 'fill');

  // Long forms are hard to read in a capped dock — the top grip resizes it
  // (drag up for more room, clamped to 160px…80vh).
  const [askPanelHeight, setAskPanelHeight] = useState(null); // null = auto (≤44vh)
  const askResizeRef = useRef(null);
  const startAskResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = askResizeRef.current ? askResizeRef.current.getBoundingClientRect().height : 300;
    const onMove = (ev) => {
      // Clamp to 60% of the viewport — the dock is an assist, not a takeover;
      // the conversation must stay visible behind it.
      const next = Math.min(Math.max(startH + (startY - ev.clientY), 160), Math.round(window.innerHeight * 0.6));
      setAskPanelHeight(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ONE connected panel for every pending ask — same width as the composer it
  // sits on, integrated grip header (drag to resize) with the step label, and
  // the cards render borderless inside so it reads as a single surface.
  const pendingAskPanel = pendingAsk && (
    <div
      className="relative w-full max-w-[760px] mx-auto mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200 rounded-2xl border bg-white overflow-hidden"
      style={{ borderColor: '#ddd6c6', boxShadow: '0 10px 30px -12px rgba(8,17,31,0.18)' }}
    >
      {/* Resize handle (top edge) — the SAME affordance as the artifact
          panel's edge handle: invisible hit strip, 3px pill, teal on hover. */}
      <div
        onPointerDown={startAskResize}
        className="absolute top-0 left-0 right-0 h-3 cursor-row-resize flex items-center justify-center z-20 group/rz"
        aria-hidden="true"
      >
        <div className="h-[3px] w-14 rounded-full bg-[#ddd6c6] group-hover/rz:bg-[#145f5f] transition-colors" />
      </div>
      <div className="pt-3 px-3.5 -mb-2 select-none">
        <span className="text-[9.5px] uppercase tracking-[0.6px] font-bold" style={{ color: '#8a6a1f' }}>
          {pendingAsk.step}
        </span>
      </div>
      <div
        ref={askResizeRef}
        className="overflow-y-auto cs-content-scroll"
        style={askPanelHeight ? { height: askPanelHeight } : { maxHeight: '44vh' }}
      >
        {pendingAsk.type === 'web' ? (
          <WebSearchOfferCard
            flat
            onAllow={() => handleAllowWebSearch(pendingAsk.msgIndex)}
            onDismiss={() => handleDismissWebOffer(pendingAsk.msgIndex)}
          />
        ) : pendingAsk.type === 'details' ? (
          <DetailsConfirmCard
            flat
            card={pendingAsk.card}
            onConfirm={(changed, hasEdits) => handleConfirmDetails(pendingAsk.msgIndex, pendingAsk.cardIndex, pendingAsk.card, changed, hasEdits)}
            // Parity with CopilotDock: a card cannot be answered mid-turn. Its
            // submit would land behind the reply already in flight.
            disabled={loading}
          />
        ) : (
          <FactsFormCard
            // Remount when the ask changes. The card seeds its state from the
            // prefilled field values ON MOUNT ONLY, so reusing one instance
            // across a facts→fill transition (or a second draft) would strand
            // the previous card's answers in the new form.
            key={`${pendingAsk.type}:${pendingAsk.msgIndex}:${(pendingAsk.card.fields || []).map((f) => f.key).join('|')}`}
            flat
            card={pendingAsk.card}
            onSubmit={(values, other) => (pendingAsk.type === 'facts'
              ? handleSubmitFacts(pendingAsk.msgIndex, pendingAsk.cardIndex, pendingAsk.card, values, other)
              : handleFillPlaceholders(pendingAsk.msgIndex, pendingAsk.draft, values, other))}
            onSkip={() => (pendingAsk.type === 'facts'
              ? handleSkipFacts(pendingAsk.msgIndex, pendingAsk.cardIndex)
              : handleDismissFill(pendingAsk.msgIndex, (pendingAsk.card.fields || []).map((f) => f.key), pendingAsk.draft))}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );

  // The composer (input + send + privacy). Rendered centered in the hero and
  // docked at the bottom once the conversation starts. A plain render helper
  // (not a component) so the textarea keeps focus across parent re-renders.
  const composer = () => (
    <div className="w-full max-w-[760px] mx-auto">
      <div
        className="relative rounded-[22px] border bg-white transition-all duration-200 focus-within:border-[#145f5f] focus-within:shadow-[0_0_0_3px_rgba(20,95,95,0.12)]"
        style={{ borderColor: dragOver ? '#145f5f' : '#ddd6c6', boxShadow: dragOver ? '0 0 0 3px rgba(20,95,95,0.14)' : '0 4px 20px -8px rgba(8,17,31,0.12)' }}
        onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={onDrop}
      >
        {/* Hidden file input — opened by the paperclip button. */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ATTACH_ACCEPT}
          multiple
          className="hidden"
          onChange={onFilePick}
        />
        {/* Attachment chips — extracted in-memory; only the text is sent. */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2.5 px-3.5 pt-4 pb-1">
            {attachments.map((a) => <AttachmentChip key={a.id} att={a} onRemove={removeAttachment} onOpen={() => setPreviewId(a.id)} />)}
          </div>
        )}
        {/* A prompt queued while the AI is still replying — auto-sends on finish. */}
        {queuedText && (
          <div className="mx-3.5 mt-3 rounded-xl border px-3 py-2 flex items-start gap-2.5" style={{ borderColor: 'rgba(184,151,58,0.4)', background: 'rgba(184,151,58,0.06)' }}>
            <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(184,151,58,0.14)', color: '#8a6a1f' }}>
              <FiClock size={12} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] uppercase tracking-[0.6px] font-semibold text-[#8a6a1f]">Queued — sends when this reply finishes</div>
              <div className="text-[12px] leading-snug text-[#3f4b5e] mt-0.5" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{queuedText}</div>
            </div>
            <button
              type="button"
              onClick={() => { setInput((v) => (v ? `${queuedText}\n${v}` : queuedText)); setQueuedText(null); setTimeout(() => textareaRef.current?.focus(), 0); }}
              className="text-[10.5px] font-semibold text-[#8a6a1f] hover:underline shrink-0 mt-0.5"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setQueuedText(null)}
              className="w-6 h-6 rounded-md flex items-center justify-center text-[#9a9080] hover:text-[#08111f] hover:bg-[#f0ece2] transition-colors shrink-0"
              aria-label="Cancel queued message"
            >
              <FiX size={13} />
            </button>
          </div>
        )}
        {/* Quoted artifact passage — a dedicated context chip (Copilot-style).
            The AI receives it in front of whatever the user types below.
            Click the excerpt to expand it; "Show in draft" flashes the passage
            inside the open document. */}
        {quotedSelection && (
          <div className="mx-3.5 mt-3 rounded-xl border px-3 py-2" style={{ borderColor: 'rgba(20,95,95,0.35)', background: 'rgba(20,95,95,0.05)' }}>
            <div className="flex items-start gap-2.5">
              <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(20,95,95,0.1)', color: '#145f5f' }}>
                <FiFileText size={12} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[9.5px] uppercase tracking-[0.6px] font-semibold text-[#145f5f]">Selected from the draft</span>
                  {activeArtifact && (
                    <button
                      type="button"
                      onClick={() => setHighlightReq({ text: quotedSelection, nonce: Date.now() })}
                      className="text-[10px] font-semibold text-[#145f5f] hover:underline"
                    >
                      Show in draft
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setQuoteExpanded((v) => !v)}
                  title={quoteExpanded ? 'Collapse' : 'Click to see the full selection'}
                  className="block w-full text-left text-[12px] leading-snug text-[#3f4b5e] mt-0.5 cursor-pointer"
                  style={quoteExpanded
                    ? { maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap' }
                    : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {quotedSelection}
                </button>
              </div>
              <button
                type="button"
                onClick={() => { setQuotedSelection(null); setQuoteExpanded(false); }}
                className="w-6 h-6 rounded-md flex items-center justify-center text-[#9a9080] hover:text-[#08111f] hover:bg-[#f0ece2] transition-colors shrink-0"
                aria-label="Remove selection"
              >
                <FiX size={13} />
              </button>
            </div>
          </div>
        )}
        {/* Automatic live markdown preview — appears on its own the moment the
            text contains markdown (bold, lists, headings, code, tables…), so
            you see how the message will render as you type, Notion-style. No
            toggle: plain prose shows nothing, markdown shows the preview. */}
        {looksLikeMarkdown(input) && (
          <div className="mx-3.5 mt-3 rounded-xl border px-3 py-2" style={{ borderColor: '#eee6d8', background: '#faf8f3' }}>
            <div
              className="chat-md text-[13px] leading-relaxed text-[#1a2230]"
              style={{ maxHeight: 180, overflowY: 'auto' }}
              dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(marked.parse(input, { breaks: true })) }}
            />
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInput}
          onKeyDown={(e) => handleKeyDown(e, sendTyped)}
          onPaste={onPaste}
          placeholder={dragOver ? 'Drop files to attach…' : loading ? 'Type your next message — it sends when this reply finishes…' : quotedSelection ? 'Ask about or request a change to the selected text…' : askIsSuperseding ? 'Or type here instead — this skips the questions above' : 'Ask a compliance question or request a document draft…'}
          rows={1}
          className="w-full resize-none bg-transparent pl-4 pr-3 pt-3.5 pb-2 text-[14px] leading-relaxed outline-none disabled:opacity-60 composer-scroll"
          style={{ maxHeight: '200px', fontFamily: "'Outfit', sans-serif" }}
        />
        <div className="flex items-center justify-between gap-2 px-3 pb-2.5 pt-0.5">
          <div className="flex items-center gap-2 min-w-0">
            {/* Attach files */}
            <div className="relative">
              <HoverPopover variant="tooltip" content="Attach — upload a file, or open a saved document" disabled={attachMenuOpen}>
                <button
                  type="button"
                  onClick={() => setAttachMenuOpen((o) => !o)}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-[#7a7060] hover:text-[#145f5f] hover:bg-[#f0ece2] transition-colors"
                  style={attachMenuOpen ? { background: '#f0ece2', color: '#145f5f' } : undefined}
                  aria-label="Attach"
                >
                  <FiPaperclip size={15} />
                </button>
              </HoverPopover>
              {attachMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAttachMenuOpen(false)} />
                  <div className="absolute bottom-full mb-2 left-0 z-50 w-[230px] rounded-xl border bg-white p-1.5 flex flex-col" style={{ borderColor: '#e2d9c8', boxShadow: '0 12px 32px -8px rgba(8,17,31,0.2)' }}>
                    <button
                      type="button"
                      disabled={attachments.length >= MAX_ATTACHMENTS}
                      onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-[#faf7f2] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(20,95,95,0.08)] text-[#145f5f]"><FiUpload size={13} /></span>
                      <span className="min-w-0">
                        <span className="block text-[12px] font-semibold text-[#08111f]">Upload a file</span>
                        <span className="block text-[10px] text-[#9a9080]">{attachments.length >= MAX_ATTACHMENTS ? `Up to ${MAX_ATTACHMENTS} files` : 'PDF, image, TXT, DOCX'}</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAttachMenuOpen(false); setDraftPickerOpen(true); }}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-[#faf7f2] transition-colors"
                    >
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(138,106,31,0.1)] text-[#8a6a1f]"><FiFolder size={13} /></span>
                      <span className="min-w-0">
                        <span className="block text-[12px] font-semibold text-[#08111f]">From My Documents</span>
                        <span className="block text-[10px] text-[#9a9080]">Open a saved draft in this chat</span>
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
            {/* Web search — the chip always states the REAL posture. In Auto it
                starts on (Vee verifies critical claims itself); flipping it here
                is an explicit choice that sticks and overrides the mode. */}
            <HoverPopover
              variant="tooltip"
              content={webSearchOn
                ? 'Web search ON — Vee looks things up to be sure and cites what it used; results can still be outdated'
                : 'Web search OFF — Vee won\'t look anything up. It\'ll say when it can\'t be sure, and offer to check'}
            >
              <button
                type="button"
                onClick={() => toggleWebSearch()}
                aria-label="Toggle web search"
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 h-7 rounded-lg transition-colors"
                style={webSearchOn ? { color: '#145f5f', background: '#e3f0ed' } : { color: '#7a7060' }}
              >
                <FiGlobe size={13} /> {webSearchOn ? 'Web on' : 'Web'}
              </button>
            </HoverPopover>
            {/* Drafting mode: Auto (drafts now, looks things up, asks after) vs
                Guided (one confirm round — details card + questions — first).
                Value stays 'quick' on the wire; 'Auto' is the label only.
                Stays neutral in both modes — neither is "enabled", and the
                label already names the current one. */}
            <HoverPopover
              variant="tooltip"
              content={draftMode === 'guided'
                ? 'Guided — Vee confirms your company details and asks everything once BEFORE drafting, and won\'t search the web on its own. Click for Auto'
                : 'Auto — Vee drafts straight away, looks things up as needed, and asks for missing bits after. Click for Guided'}
            >
              <button
                type="button"
                onClick={() => setDraftMode((m) => (m === 'guided' ? 'quick' : 'guided'))}
                aria-label="Toggle drafting mode"
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 h-7 rounded-lg transition-colors"
                style={{ color: '#7a7060' }}
              >
                <FiCheck size={13} /> {draftMode === 'guided' ? 'Guided' : 'Auto'}
              </button>
            </HoverPopover>
            {/* Privacy / history popover */}
            <div className="relative">
              <HoverPopover
                variant="tooltip"
                content={consentPersist
                  ? 'History ON — chats are saved. Click to change or go private'
                  : 'Private mode — nothing is saved. Click to turn history on'}
                disabled={privacyOpen}
              >
                <button
                  type="button"
                  onClick={() => setPrivacyOpen((o) => !o)}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg transition-colors"
                  style={{ color: consentPersist ? '#145f5f' : '#7a7060', background: privacyOpen ? '#f0ece2' : 'transparent' }}
                >
                  <FiShield size={12} /> {consentPersist ? 'History on' : 'Private'}
                </button>
              </HoverPopover>
              {privacyOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setPrivacyOpen(false)} />
                  <div className="absolute bottom-full mb-2 left-0 z-50 w-[280px] rounded-xl border bg-white p-3 flex flex-col gap-2.5" style={{ borderColor: '#e2d9c8', boxShadow: '0 12px 32px -8px rgba(8,17,31,0.2)' }}>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={consentPersist} onChange={(e) => setConsentPersist(e.target.checked)} className="mt-0.5 accent-[#145f5f]" />
                      <span className="text-[11.5px] text-[#5a5145]">
                        Save my chat history <span className="text-[#9a9080]">(kept {HISTORY_WINDOW_LABELS[user?.aiPreferences?.historyAutoDelete] || '36 months'}, deletable)</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={consentTrain} onChange={(e) => setConsentTrain(e.target.checked)} className="mt-0.5 accent-[#145f5f]" />
                      <span className="text-[11.5px] text-[#5a5145]">
                        Use my data to improve Vee
                        <span className="block text-[10px] text-[#9a9080]">Identifiers removed; never changes how long chats are kept.</span>
                      </span>
                    </label>
                    <div className="text-[10px] text-[#9a9080]">
                      Uncheck history to go private — this chat won’t be saved. Auto-delete window and full details live in{' '}
                      <button type="button" className="underline text-[#145f5f]" onClick={() => navigate(`${ROUTES.PROFILE}?tab=privacy`)}>Profile → Privacy</button>.
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Which company THIS CHAT is on. Once one is picked it is the
                answer and reads as one; until then the chip shows the app's
                selector, which is only where the user is standing — the same
                distinction the model is given, so the UI cannot promise a
                binding the backend does not honour. */}
            {/* ONLY the company this conversation is bound to.
                The app's selector deliberately does not appear here. It always
                points somewhere — it auto-pins the first company in the
                portfolio — so showing its name beside the composer told every
                user their chat was about a company nobody had chosen. Greying
                it was not enough: a company name sitting under the message box
                reads as THE company whatever colour it is, and the tooltip
                explaining otherwise is invisible until hovered. The sidebar
                already shows where the user is standing; repeating it here only
                promised a binding the backend does not honour. */}
            {threadCompany?.name && (
              <span
                className="text-[11px] truncate max-w-[170px] font-medium"
                style={{ color: '#145f5f' }}
                title={`This chat is about ${threadCompany.name}. Documents will be drafted for it.`}
              >
                · {threadCompany.name}
              </span>
            )}
            {!threadCompany && noCompanyMode && (
              <span
                className="text-[11px] truncate max-w-[170px] font-medium"
                style={{ color: '#7a7060' }}
                title="No company selected. General questions and rule-book answers work as usual; a document will be drafted with placeholders unless you name a company."
              >
                · No company
              </span>
            )}
          </div>
          {/* Doubles as Stop while generating (Claude-style): the provider keeps
              streaming even off-page, so an explicit stop is the only cancel. */}
          <button
            type="button"
            onClick={loading ? stopGenerating : () => sendTyped()}
            disabled={loading ? false : (!input.trim() || extractingCount > 0)}
            title={loading ? 'Stop generating' : (extractingCount > 0 ? 'Reading attached file…' : undefined)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0 transition-all duration-150 active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: '#145f5f' }}
            aria-label={loading ? 'Stop generating' : 'Send message'}
          >
            {loading
              ? <span className="w-3 h-3 rounded-[3px] bg-white" />
              : <FiArrowUp size={17} />}
          </button>
        </div>
      </div>
    </div>
  );

  const disclaimer = (
    <div className="text-[10.5px] text-center text-[#9a9080]">
      Vee can make mistakes — verify with a qualified CA/CS before filing or execution.
    </div>
  );

  return (
    <AppLayout pageTitle="Vee AI" fullHeight flush>
      {/* Markdown styling for assistant turns — tuned for document-like reading. */}
      <style>{`
        .chat-md > :first-child { margin-top: 0; }
        .chat-md > :last-child { margin-bottom: 0; }
        .chat-md p { margin: 0 0 10px; }
        .chat-md ul, .chat-md ol { margin: 8px 0; padding-left: 20px; }
        .chat-md li { margin: 4px 0; }
        .chat-md strong { font-weight: 700; color: #08111f; }
        .chat-md code { background: rgba(8,17,31,0.06); padding: 1.5px 5px; border-radius: 5px; font-size: 12.5px; font-family: 'JetBrains Mono', monospace; }
        .chat-md h1, .chat-md h2, .chat-md h3 { font-weight: 700; margin: 14px 0 6px; font-size: 15px; color: #08111f; font-family: 'Cormorant Garamond', serif; }
        .chat-md h1 { font-size: 19px; }
        .chat-md a { color: #145f5f; text-decoration: underline; }
        .chat-md blockquote { border-left: 3px solid #ddd6c6; padding-left: 12px; margin: 8px 0; color: #5a5145; }
        .chat-md table { border-collapse: collapse; margin: 8px 0; font-size: 13px; width: 100%; }
        .chat-md th, .chat-md td { border: 1px solid #e2d9c8; padding: 5px 9px; text-align: left; }
        .chat-md th { background: #faf8f3; font-weight: 700; }
        @keyframes heroGlow { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.06); } }
        .hero-glow { animation: heroGlow 4.5s ease-in-out infinite; }
        .composer-scroll { scrollbar-width: thin; scrollbar-color: rgba(122,112,96,0.32) transparent; }
        .composer-scroll::-webkit-scrollbar { width: 11px; }
        .composer-scroll::-webkit-scrollbar-track { background: transparent; margin: 10px 0; }
        .composer-scroll::-webkit-scrollbar-button { display: none; height: 0; width: 0; }
        .composer-scroll::-webkit-scrollbar-thumb { background: rgba(122,112,96,0.3); border-radius: 999px; border: 3.5px solid transparent; background-clip: padding-box; }
        .composer-scroll:hover::-webkit-scrollbar-thumb { background: rgba(122,112,96,0.42); }
      `}</style>

      <div className="h-full flex">
        {/* ── Chat column ── */}
        <div className="flex-1 min-w-0 flex flex-col relative">
          {/* Top toolbar — New chat + History (floats top-right, no layout space) */}
          <div className="absolute top-3 right-4 z-20 flex items-center gap-1 rounded-xl px-1 py-1 backdrop-blur-sm" style={{ background: 'rgba(244,240,232,0.72)' }}>
            {started && (
              <button
                type="button"
                onClick={newChat}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 h-8 rounded-lg text-[#5a5145] hover:bg-[#f0ece2] transition-colors active:scale-95"
              >
                <FiPlus size={13} /> New chat
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(historyOpen ? ROUTES.AGENT : ROUTES.AGENT_HISTORY)}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 h-8 rounded-lg transition-colors active:scale-95 hover:bg-[#f0ece2]"
              style={historyOpen ? { background: '#145f5f', color: '#fff' } : { color: '#5a5145' }}
            >
              <FiClock size={13} /> History
            </button>
          </div>

          {historyOpen ? (
            /* Dedicated history view — takes over the chat area */
            <div className="flex-1 overflow-y-auto cs-content-scroll animate-in fade-in duration-200">
              <div className="max-w-[860px] mx-auto px-6 py-7">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h2 className="text-[26px] leading-tight text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Conversation history</h2>
                  <Button variant="ghost" size="sm" leftIcon={<FiX size={15} />} onClick={closeHistory}>Close</Button>
                </div>
                <p className="text-[12.5px] text-[#7a7060] mb-4">
                  {historyTab === 'deleted'
                    ? 'Deleted conversations are kept for 7 days, then permanently removed. Restore one anytime before then.'
                    : historySessions.length > 0
                      ? `${historySessions.length} saved conversation${historySessions.length === 1 ? '' : 's'} — click one to reopen it.`
                      : 'Your saved conversations with Vee appear here.'}
                </p>

                {/* Tabs: live history vs the Recently Deleted bin */}
                <div className="flex items-center gap-1 mb-5">
                  <button
                    type="button"
                    onClick={() => setHistoryTab('all')}
                    className="text-[12px] font-semibold px-3 h-8 rounded-lg transition-colors"
                    style={historyTab === 'all' ? { background: '#145f5f', color: '#fff' } : { color: '#5a5145' }}
                  >
                    All chats
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoryTab('deleted')}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 h-8 rounded-lg transition-colors"
                    style={historyTab === 'deleted' ? { background: '#145f5f', color: '#fff' } : { color: '#5a5145' }}
                  >
                    <FiTrash2 size={12} /> Recently deleted{deletedSessions.length > 0 ? ` (${deletedSessions.length})` : ''}
                  </button>
                  {/* Per-surface scope chip (e.g. opened from the Minutes studio) */}
                  {historyKindFilter && historyTab === 'all' && (
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold h-7 pl-2.5 pr-1.5 rounded-full border"
                      style={{ borderColor: 'rgba(20,95,95,0.3)', background: 'rgba(20,95,95,0.06)', color: '#145f5f' }}
                    >
                      {historyKindFilter === 'minutes' ? 'Minutes chats' : `${historyKindFilter} chats`}
                      <button
                        type="button"
                        aria-label="Show all conversations"
                        title="Show all conversations"
                        onClick={() => {
                          setHistoryKindFilter(null);
                          // Clear the navigation state too, or reopening keeps re-applying it.
                          navigate(ROUTES.AGENT_HISTORY, { replace: true });
                        }}
                        className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-[rgba(20,95,95,0.15)]"
                      >
                        <FiX size={11} />
                      </button>
                    </span>
                  )}
                  {historyTab === 'all' && historySessions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteAll(true)}
                      className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 h-8 rounded-lg transition-colors hover:bg-[rgba(154,86,74,0.08)]"
                      style={{ color: '#9a564a' }}
                    >
                      <FiTrash2 size={12} /> Delete all
                    </button>
                  )}
                </div>

                {historyTab === 'deleted' ? (
                  deletedLoading ? (
                    <div className="flex items-center justify-center gap-2 text-[13px] text-[#7a7060] py-20">
                      <span className="w-4 h-4 rounded-full border-2 border-[#ddd6c6] border-t-[#145f5f] animate-spin" /> Loading…
                    </div>
                  ) : deletedSessions.length === 0 ? (
                    <div className="rounded-2xl border bg-white p-12 flex flex-col items-center text-center gap-2.5" style={{ borderColor: 'rgba(226,217,200,0.7)' }}>
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(154,86,74,0.08)', color: '#9a564a' }}><FiTrash2 size={20} /></div>
                      <div className="text-[15px] font-semibold text-[#08111f]">Nothing in Recently Deleted</div>
                      <div className="text-[12.5px] text-[#7a7060] max-w-[420px] leading-relaxed">Conversations you delete land here for 7 days so you can restore them before they’re gone for good.</div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {deletedSessions.map((s) => (
                        <div key={s.id} className="group/hc w-full rounded-xl border bg-white px-4 py-3 flex items-center gap-3.5" style={{ borderColor: '#e2d9c8' }}>
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(154,86,74,0.08)] text-[#9a564a]"><FiMessageSquare size={15} /></div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-[#08111f] truncate"><SessionTitle title={s.title} /></div>
                            {s.preview && <div className="text-[11.5px] text-[#7a7060] truncate mt-0.5">{s.preview}</div>}
                          </div>
                          <div className="shrink-0 text-[11px] font-medium text-[#9a564a] hidden sm:block mr-1">{purgeLabel(s)}</div>
                          <button type="button" onClick={() => restoreSession(s.id)} className="shrink-0 text-[12px] font-semibold px-3 h-8 rounded-lg text-[#145f5f] hover:bg-[#e8f4f4] transition-colors">Restore</button>
                          <span role="button" tabIndex={-1} onClick={() => askPermanentDelete(s.id)} className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[#9a9080] hover:text-[#9a564a] hover:bg-[#f3ebe9] transition-all" aria-label="Delete permanently"><FiTrash2 size={14} /></span>
                        </div>
                      ))}
                    </div>
                  )
                ) : historyLoading ? (
                  <div className="flex items-center justify-center gap-2 text-[13px] text-[#7a7060] py-20">
                    <span className="w-4 h-4 rounded-full border-2 border-[#ddd6c6] border-t-[#145f5f] animate-spin" /> Loading conversations…
                  </div>
                ) : historySessions.length === 0 ? (
                  <div className="rounded-2xl border bg-white p-12 flex flex-col items-center text-center gap-2.5" style={{ borderColor: 'rgba(226,217,200,0.7)' }}>
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(20,95,95,0.08)', color: '#145f5f' }}><FiMessageSquare size={22} /></div>
                    <div className="text-[15px] font-semibold text-[#08111f]">No saved conversations yet</div>
                    <div className="text-[12.5px] text-[#7a7060] max-w-[420px] leading-relaxed">Start a conversation and it’ll be saved here automatically — every chat and the documents it generates. (You can go private anytime from the composer’s privacy menu.)</div>
                  </div>
                ) : (
                  <>
                    {/* Search */}
                    <div className="relative mb-5">
                      <FiSearch size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9a9080]" />
                      <input
                        value={historyQuery}
                        onChange={(e) => setHistoryQuery(e.target.value)}
                        placeholder="Search conversations…"
                        className="w-full h-11 pl-10 pr-3.5 rounded-xl border bg-white text-[13px] outline-none transition-all focus:border-[#145f5f] focus:shadow-[0_0_0_3px_rgba(20,95,95,0.12)]"
                        style={{ borderColor: '#ddd6c6' }}
                      />
                    </div>

                    {(() => {
                      const groups = groupSessions(historySessions, historyQuery);
                      if (groups.length === 0) {
                        return <div className="text-[12.5px] text-[#7a7060] text-center py-12">No conversations match “{historyQuery}”.</div>;
                      }
                      return groups.map((g) => (
                        <div key={g.label} className="mb-6 last:mb-0">
                          <div className="text-[10.5px] font-bold uppercase tracking-[0.8px] text-[#9a9080] mb-2.5 px-1">{g.label}</div>
                          <div className="flex flex-col gap-2">
                            {g.items.map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                // Conversations belong to the surface that STARTED
                                // them: a chat born in the Minutes studio reopens
                                // there (dock + document side by side); agent-born
                                // chats open right here.
                                onClick={() => (s.origin === 'minutes'
                                  ? navigate(ROUTES.MINUTES, { state: { openChatSessionId: s.id } })
                                  : s.origin === 'report'
                                    ? navigate(ROUTES.REPORT_GENERATOR, { state: { openChatSessionId: s.id } })
                                    : loadSession(s.id))}
                                className="group/hc w-full text-left rounded-xl border bg-white px-4 py-3 flex items-center gap-3.5 transition-all duration-200 hover:border-[#145f5f] hover:shadow-[0_8px_22px_-15px_rgba(20,95,95,0.32)]"
                                style={{ borderColor: '#e2d9c8' }}
                              >
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(20,95,95,0.08)] text-[#145f5f] group-hover/hc:bg-[#145f5f] group-hover/hc:text-white transition-colors"><FiMessageSquare size={15} /></div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <div className="text-[13px] font-semibold text-[#08111f] truncate"><SessionTitle title={s.title} /></div>
                                    {s.docCount > 0 && (
                                      <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(20,95,95,0.08)', color: '#145f5f' }}>
                                        <FiFileText size={10} /> {s.docCount}
                                      </span>
                                    )}
                                  </div>
                                  {s.preview && <div className="text-[11.5px] text-[#7a7060] truncate mt-0.5">{s.preview}</div>}
                                </div>
                                <div className="shrink-0 text-right hidden sm:block">
                                  <div className="text-[11px] font-medium text-[#5a5145]"><TimeAgo iso={s.updatedAt}>{timeAgo(s.updatedAt)}</TimeAgo></div>
                                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#9a9080] mt-0.5">{s.messageCount} msg{s.messageCount === 1 ? '' : 's'}</div>
                                </div>
                                <span role="button" tabIndex={-1} onClick={(e) => askDeleteSession(s.id, e)} className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[#b7ae9c] hover:text-[#9a564a] hover:bg-[#f3ebe9] opacity-80 hover:opacity-100 transition-all" aria-label="Delete conversation"><FiTrash2 size={14} /></span>
                                <FiChevronRight size={16} className="shrink-0 text-[#c9c0ad] group-hover/hc:text-[#145f5f] transition-colors hidden md:block" />
                              </button>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </>
                )}
              </div>
            </div>
          ) : !started ? (
            /* Empty-state hero */
            <div className="relative flex-1 overflow-y-auto cs-content-scroll">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-[14%] -translate-x-1/2 w-[560px] h-[560px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(20,95,95,0.10) 0%, rgba(184,151,58,0.05) 35%, transparent 65%)', filter: 'blur(16px)' }}
              />
              <div className="relative min-h-full flex flex-col items-center justify-center px-4 py-10">
                <div className="w-full max-w-[760px] flex flex-col items-center animate-in fade-in slide-in-from-bottom-3 duration-700 ease-out">
                  <div className="relative mb-6">
                    <div className="hero-glow absolute -inset-4 rounded-full blur-2xl" style={{ background: 'radial-gradient(circle, rgba(184,151,58,0.45), rgba(20,95,95,0.16) 55%, transparent 72%)' }} />
                    <div className="relative w-[72px] h-[72px] rounded-[24px] overflow-hidden" style={{ boxShadow: '0 16px 38px -12px rgba(8,17,31,0.36), 0 0 0 1px rgba(184,151,58,0.16)' }}>
                      <img src="/vee-mascot.svg" alt="Vee" className="w-full h-full object-contain" />
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 mb-4">
                    <span className="block h-px w-8" style={{ background: 'linear-gradient(to right, transparent, rgba(184,151,58,0.55))' }} />
                    <span className="text-[10px] font-bold uppercase tracking-[2.5px] text-[#b8973a]">Vee AI</span>
                    <span className="block h-px w-8" style={{ background: 'linear-gradient(to left, transparent, rgba(184,151,58,0.55))' }} />
                  </div>

                  <h1 className="text-[36px] leading-[1.05] text-center text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {firstName ? `Namaste, ${firstName}` : 'Namaste — I\'m Vee'}
                  </h1>
                  <p className="text-[14px] text-[#7a7060] text-center mt-3 mb-8 max-w-[480px] leading-relaxed">
                    Your AI compliance expert — ask a question, draft a document, or get guidance under <span className="font-medium text-[#5a5145]">Companies Act, SEBI, FEMA &amp; DPDP</span>.
                  </p>

                  {composer()}

                  <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-6">
                    {SUGGESTIONS.map((s) => {
                      const Ico = s.icon;
                      return (
                        <button
                          key={s.prompt}
                          // sendTyped for the same reason the composer uses it.
                          // These chips only render on the empty-state hero, so
                          // no card can be pending — routing them through the
                          // one user-send path keeps that a fact of the layout
                          // rather than something to re-derive later.
                          onClick={() => sendTyped(s.prompt)}
                          disabled={loading}
                          className="group text-left rounded-2xl border bg-white p-4 flex items-start gap-3 transition-all duration-200 hover:border-[#145f5f] hover:shadow-[0_10px_28px_-14px_rgba(20,95,95,0.35)] hover:-translate-y-0.5 active:scale-[0.99] disabled:opacity-50"
                          style={{ borderColor: '#e2d9c8' }}
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200 bg-[rgba(20,95,95,0.08)] text-[#145f5f] group-hover:bg-[#145f5f] group-hover:text-white">
                            <Ico size={15} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1 text-[12px] font-semibold text-[#08111f]">
                              {s.label}
                              <span className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 text-[#145f5f]">→</span>
                            </div>
                            <div className="text-[11.5px] text-[#7a7060] leading-snug mt-0.5">{s.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-7">{disclaimer}</div>
                </div>
              </div>
            </div>
          ) : (
            /* Conversation */
            <>
              <div className="flex-1 overflow-y-auto cs-content-scroll animate-in fade-in duration-300">
                <ChatThread
                  messages={messages}
                  endRef={messagesEndRef}
                  onCopy={copyMessage}
                  onOpenArtifact={setActiveArtifact}
                  activeArtifact={activeArtifact}
                  onConfirmProposal={handleConfirmProposal}
                  onFeedback={handleFeedback}
                  onExport={handleExport}
                  onEditResend={handleEditResend}
                  onSwitchVersion={handleSwitchVersion}
                  onBranchFrom={handleBranchFrom}
                  onNavigateSource={navigate}
                  onOpenAttachment={(m, a) => setSentPreview({
                    // Whole turn — images AND files — so the lightbox arrows
                    // page across everything that was sent together.
                    items: m.attachments,
                    id: a.id,
                  })}
                  canEdit={!loading}
                />
              </div>
              {/* Floating dock — no separator line; the composer's own rounded
                  border + shadow carry the "floating over the chat" look. A
                  pending ask-form docks directly above it, Claude-style. */}
              <div className="pt-1.5 pb-2 px-4">
                {pendingAskPanel}
                {composer()}
                <div className="mt-2">{disclaimer}</div>
              </div>
            </>
          )}
        </div>

        {/* ── Artifact side panel ── */}
        {activeArtifact && (
          <ArtifactPanel
            artifact={activeArtifact}
            onClose={() => setActiveArtifact(null)}
            onSave={handleSaveDraft}
            onEdited={handleArtifactEdited}
            onQuote={handleQuoteSelection}
            onOpenStudio={handleOpenStudio}
            highlight={highlightReq}
          />
        )}
      </div>

      {/* ── Lightbox over a sent message's images (no Remove — it's history) ── */}
      {sentPreview && (
        <AttachmentPreview
          items={sentPreview.items}
          currentId={sentPreview.id}
          onSelect={(id) => setSentPreview((p) => (p ? { ...p, id } : p))}
          onClose={() => setSentPreview(null)}
        />
      )}

      {/* ── Attachment preview overlay (navigable across all attachments) ── */}
      {previewAtt && (
        <AttachmentPreview
          items={attachments}
          currentId={previewId}
          onSelect={setPreviewId}
          onClose={() => setPreviewId(null)}
          onRemove={removeAttachment}
        />
      )}

      {/* ── Delete confirmation (soft delete → Recently Deleted, or permanent) ── */}
      <ConfirmDialog
        open={!!confirm}
        variant="danger"
        loading={!!confirm?.busy}
        onClose={() => { if (!confirm?.busy) setConfirm(null); }}
        onConfirm={runConfirm}
        title={confirm?.kind === 'permanent' ? 'Delete permanently?' : 'Delete this conversation?'}
        message={confirm?.kind === 'permanent'
          ? 'This conversation will be erased immediately and cannot be recovered.'
          : 'It will move to Recently Deleted and be kept for 7 days — you can restore it any time before then.'}
        note={confirm?.kind === 'permanent' ? null : 'Find it under History → Recently deleted.'}
        confirmLabel={confirm?.kind === 'permanent' ? 'Delete permanently' : 'Delete'}
      />

      <ConfirmDialog
        open={confirmDeleteAll}
        variant="danger"
        loading={deletingAll}
        onClose={() => { if (!deletingAll) setConfirmDeleteAll(false); }}
        onConfirm={handleDeleteAllChats}
        title="Delete ALL chat history?"
        message="Every conversation — including Recently Deleted — is erased immediately, along with the chat images stored on our servers and this device. This cannot be undone."
        note="Backups clear within 30 days. Your saved documents in My Documents are not affected."
        confirmLabel="Delete everything"
        cancelLabel="Keep my chats"
      />

      {/* ── Open a saved document in this chat (docs-as-artifacts) ── */}
      <AttachDraftPicker
        open={draftPickerOpen}
        onClose={() => setDraftPickerOpen(false)}
        onPick={(doc, err) => {
          setDraftPickerOpen(false);
          if (doc) seedDraftIntoChat(doc);
          else showToast(err || 'Could not load that document.', 'error');
        }}
        onPickTemplate={(tpl) => {
          setDraftPickerOpen(false);
          seedTemplateIntoChat(tpl);
        }}
        onPickRecipe={(r) => {
          setDraftPickerOpen(false);
          // sendTyped, not sendMessage: picking a template is the user choosing
          // a new direction mid-conversation, so it supersedes a pending
          // ask-card exactly as typing would. Queues automatically while a
          // reply is streaming.
          sendTyped(`Draft using my template "${r.name}"`);
        }}
      />

      {/* ── Save choice for library docs: update the original, or keep a copy.
             Styled to match ConfirmDialog exactly (icon tile, serif title, note
             box, close ✕, side-by-side actions) — one dialog language app-wide. ── */}
      {saveChoice && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
          style={{ background: 'rgba(8,17,31,0.55)' }}
          onClick={(e) => { if (e.target === e.currentTarget) resolveSaveChoice('cancel'); }}
        >
          <div
            className="relative bg-white rounded-2xl w-[400px] max-w-[92vw] overflow-hidden"
            style={{ boxShadow: '0 12px 40px rgba(8,17,31,0.16), 0 2px 8px rgba(8,17,31,0.06)' }}
          >
            <button
              type="button"
              onClick={() => resolveSaveChoice('cancel')}
              className="absolute top-3 right-3 flex items-center justify-center w-7 h-7 rounded-full transition-colors text-[#9ca3af] hover:bg-[#f3f4f6] hover:text-[#374151]"
              aria-label="Close"
            >
              <FiX size={14} />
            </button>
            <div className="p-4">
              <div className="flex items-center justify-center rounded-xl mb-3" style={{ width: 48, height: 48, background: '#e8f4f4', color: '#145f5f' }}>
                <FiFileText size={22} />
              </div>
              <div className="text-[17px] font-bold mb-1 text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Save your changes
              </div>
              <div className="text-[13px] leading-[1.5] mb-3 text-[#7a7060]">
                “{saveChoice.draft.title}” came from My Documents. Update it in place, or keep the original and save these changes as a new copy.
              </div>
              {saveChoice.draft.refNumber && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg mb-3 text-[12px] leading-snug" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', color: '#7a7060' }}>
                  <FiFileText size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>Updating replaces {saveChoice.draft.refNumber}; a copy gets its own reference number.</span>
                </div>
              )}
              <div className="flex items-center gap-2 mt-1">
                <Button variant="ghost" size="lg" className="flex-1" onClick={() => resolveSaveChoice('copy')}>Save as copy</Button>
                <Button variant="ink" size="lg" className="flex-1" onClick={() => resolveSaveChoice('update')}>Update original</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
